/**
 * ============================================================================
 * VINPOP — Wine Recommendation Manager (Core Engine)
 * ============================================================================
 *
 * The main engine powering the wine recommendation experience.
 * Merges API scores with the Shopify product catalog, renders product cards
 * with match-percentage badges, and provides client-side filtering.
 *
 * Key features:
 *   - Dual-mode: "quiz" (taste profile quiz) + "assistant" (API injection)
 *   - Custom product card rendering with score badges
 *   - Client-side filters: country, type, price, match score
 *   - Quantity selector + Add to Cart (direct Ajax cart)
 *   - Product detail drawer (slides open from right)
 *   - Demo mode with synthetic taste profile
 *
 * Loaded from: sections/main-collection-product-grid.liquid
 *              sections/quiz-recommendations.liquid
 * Depends on:  collection-recommendations.js, taste-profile-quiz.js,
 *              wine-catalog-data (Liquid snippet)
 * CSS:         vinpop-custom.css, product-rating.css
 * Global:      window.wineManager (instance)
 * ============================================================================
 */

class WineRecommendationManager {
  constructor() {
    this.recommendations = []; // Liste combinée API + Catalogue
    this.filters = {}; // Filtres actifs
    this.container = null;
    this.templateCard = null; // Modèle de carte native pour le clonage
    this.productDataMap = new Map(); // Stocker les données produit (score, disponibilité) par ID unique
    this.mode = null; // 'quiz' or 'assistant' - tracks which collection we're on
    
    console.log('🍷 WineRecommendationManager v4.1 initialized (Dual-Mode: Quiz + Wine Assistant) [accent-2 badges]');
  }

  /**
   * Pick the best variant for cart and display: prefer paid variants, then available ones.
   */
  getPreferredVariant(product, options = {}) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;

    const requireAvailable = options.requireAvailable === true;
    const toCents = (variant) => {
      const raw = variant && variant.price !== undefined ? variant.price : 0;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const isAllowed = (variant) => !requireAvailable || variant.available !== false;

    const paidVariant = variants.find((variant) => toCents(variant) > 0 && isAllowed(variant));
    if (paidVariant) return paidVariant;

    const availableVariant = variants.find((variant) => isAllowed(variant));
    if (availableVariant) return availableVariant;

    return variants[0];
  }

  /**
   * Display/filter price in cents: prefer paid variant price when present.
   */
  getProductDisplayPrice(product) {
    const preferredVariant = this.getPreferredVariant(product, { requireAvailable: false });
    if (preferredVariant) {
      const variantPrice = Number(preferredVariant.price);
      if (Number.isFinite(variantPrice) && variantPrice > 0) return variantPrice;
    }

    const basePrice = Number(product?.price);
    if (Number.isFinite(basePrice)) return basePrice;

    return 0;
  }

  /**
   * MANUAL INJECTION METHOD FOR WINE ASSISTANT
   * Allows external scripts to directly render products without detection/storage logic
   * Usage: window.wineManager.renderAssistantResults(myWines)
   * 
   * @param {Array} products - Array of wine objects with format:
   *   [{ wine_id: "123", score: 95, distance: 0.5, recommendation_text: "Optional custom text" }]
   */
  async renderAssistantResults(products) {
    console.log('🍷 MANUAL INJECTION: Wine Assistant mode activated with', products.length, 'products');
    
    // Set mode to assistant
    this.mode = 'assistant';
    
    // Directly call renderRecommendations with the provided data
    // This bypasses all detection and storage logic
    await this.renderRecommendations(products);
    
    console.log('✅ Wine Assistant results manually injected and rendered');
  }

  /**
   * Fetch user recommendations from Wine Assistant API
   * This method retrieves wines based on user's "loved" wines history
   */
  async fetchUserRecommendations() {
    console.log('🍷 Fetching Wine Assistant recommendations for logged-in user...');
    
    try {
      const response = await fetch('https://api.vinpop.fr/api/wines/user-recommendations', {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          // Add authentication headers if needed
        },
        credentials: 'include' // Include cookies for auth
      });

      if (!response.ok) {
        throw new Error(`Wine Assistant API failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Wine Assistant data received:', data.length, 'wines');
      
      // Expected format: [{ wine_id: "123", score: 95, distance: 0.5 }, ...]
      return data;
      
    } catch (error) {
      console.error('❌ Failed to fetch Wine Assistant recommendations:', error);
      return []; // Return empty array on failure
    }
  }

  /**
   * PARTIE 2 : Rendu des recommandations
   * MÉTHODE "JS TAKEOVER" : Trouve la grille native et la remplace
   * WORKS FOR: Quiz mode, Taste Map V2 mode, and Wine Assistant mode
   * Filter system architecture: see FILTER_SYSTEM.md
   */
  async renderRecommendations(apiResponse, containerId = 'quiz-results-grid') {
    console.log(`🍷 JS TAKEOVER [${this.mode || 'unknown'}]: Starting autonomous rendering...`, apiResponse.length, 'items');
    
    // PROTECTION: Temporarily disable Filterable's popstate listener
    // The Shapes theme Filterable component listens for popstate and re-fetches server HTML,
    // which would replace #facets-filterable innerHTML and destroy our custom container.
    // We add a flag to block any concurrent Filterable re-renders during our operation.
    const filterableEl = document.getElementById('facets-filterable');
    if (filterableEl && filterableEl._x_dataStack) {
      try {
        // Mark that we're taking over rendering
        filterableEl.dataset.vinpopTakeover = 'true';
      } catch (e) { /* ignore */ }
    }
    
    // ÉTAPE 1 : Vérifier si le container custom existe déjà (cas bfcache / retour arrière)
    let customContainer = document.getElementById('js-wine-results');
    
    if (customContainer) {
      // Container existe déjà (page restaurée du cache)
      console.log('♻️ Custom container found (bfcache restore) - reusing it');
      this.container = customContainer;
      this.container.innerHTML = ''; // Vider pour re-render
    } else {
      // Container n'existe pas encore - première fois qu'on render
      // ÉTAPE 2 : Trouver la grille native existante (avec retry pour mobiles lents)
      let nativeGrid = null;
      const gridSelectors = [
        'ul.grid.gap-theme',
        '.product-grid',
        '#facets-results ul',
        '#MainContent ul.grid'
      ];
      
      for (let attempt = 0; attempt < 3; attempt++) {
        for (const selector of gridSelectors) {
          nativeGrid = document.querySelector(selector);
          if (nativeGrid) break;
        }
        if (nativeGrid) break;
        // Wait 300ms between retries for slow DOM rendering
        console.log(`⏳ Native grid not found, retry ${attempt + 1}/3...`);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      if (!nativeGrid) {
        console.error('❌ Could not find native product grid after retries');
        return;
      }
      
      console.log('✅ Native grid found:', nativeGrid.className);
      
      // ÉTAPE 3.1 : SCANNER LES PRODUITS ACHETÉS depuis la page native AVANT de supprimer la grille
      this.purchasedProductIds = new Set();
      const nativeCards = nativeGrid.querySelectorAll('[data-product-id]');
      nativeCards.forEach(card => {
        const hasBadge = card.querySelector('.badge-already-purchased');
        if (hasBadge) {
          const productId = card.getAttribute('data-product-id');
          if (productId) {
            this.purchasedProductIds.add(String(productId));
          }
        }
      });
      console.log('✅ Scanned purchased products:', this.purchasedProductIds.size, 'items');
      
      // ÉTAPE 3.2 : Capturer le template de carte - WAIT pour que Alpine.js initialise les composants
      if (!this.templateCard && nativeGrid.firstElementChild) {
        // Attendre 500ms pour que les data-island soient complètement initialisés
        await new Promise(resolve => setTimeout(resolve, 500));
        // Re-query firstElementChild after wait (DOM may have changed)
        const firstChild = nativeGrid.firstElementChild;
        if (firstChild) {
          this.templateCard = firstChild.cloneNode(true);
          console.log('✅ Template card captured for cloning (after components loaded)');
        } else {
          console.warn('⚠️ Native grid children disappeared during wait');
        }
      }
      
      if (!this.templateCard) {
        console.error('❌ No template card available — cannot render product cards');
        return;
      }
      
      // ÉTAPE 4 : Sauvegarder le parent AVANT de retirer la grille native
      const gridParent = nativeGrid.parentNode;
      const gridClassName = nativeGrid.className;
      
      // ÉTAPE 5 : Créer notre propre conteneur
      customContainer = document.createElement('ul');
      customContainer.id = 'js-wine-results';
      
      // Copier les classes de la grille native pour avoir le même style
      customContainer.className = gridClassName || 'grid gap-theme grid-cols-2 lg:grid-cols-3';
      
      // CRITIQUE: Forcer la largeur à 100% pour éviter width: 0
      customContainer.style.width = '100%';
      customContainer.style.display = 'grid';
      
      // Insérer AVANT la grille native (qui existe encore)
      gridParent.insertBefore(customContainer, nativeGrid);
      console.log('✅ Custom container created and inserted');
      
      // ÉTAPE 6 : MAINTENANT on peut supprimer la grille native en toute sécurité
      nativeGrid.remove();
      console.log('✅ Native grid removed from DOM');
      
      // ÉTAPE 7 : Supprimer les filtres natifs Shopify pour éviter les conflits avec le drawer
      // Supprimer le conteneur principal des filtres (Filter + Sort buttons)
      const nativeFiltersContainer = document.querySelector('#native-filters-container');
      if (nativeFiltersContainer) {
        nativeFiltersContainer.remove();
        console.log('✅ Native filters container removed (Filter + Sort buttons)');
      }
      
      // Supprimer aussi les autres conteneurs possibles
      const nativeFilters = document.querySelector('#facets') || 
                           document.querySelector('.facets') ||
                           document.querySelector('[data-facets]') ||
                           document.querySelector('.collection-filters');
      
      if (nativeFilters) {
        nativeFilters.remove();
        console.log('✅ Native Shopify filters removed to prevent conflicts');
      }
      
      // Supprimer la barre de tri native si présente séparément
      const nativeSortBar = document.querySelector('.facets__sort') ||
                           document.querySelector('[data-sort-by]') ||
                           document.querySelector('.collection-sort');
      
      if (nativeSortBar) {
        nativeSortBar.remove();
        console.log('✅ Native sort bar removed');
      }
      
      // Supprimer le compteur de résultats natif
      const resultsCount = document.querySelector('#facets-results-count');
      if (resultsCount) {
        resultsCount.remove();
      }
      
      this.container = customContainer;
    }
    
    // Combiner API response avec le catalogue
    // Double protection: exclure les vins hors fenêtre d'Overton
    const excludedWineIds = ['15510626763139'];
    
    const allMatched = apiResponse
      .filter(item => !excludedWineIds.includes(String(item.wine_id)))
      .map(item => {
        const productId = String(item.wine_id);
        const productData = window.shopCatalog[productId];
        
        if (!productData) {
          console.warn('⚠️ Product not found in catalog:', productId);
          return null;
        }

        // Skip only pure zero-price placeholders (all variants at 0), not mixed paid/free products.
        const hasPaidVariant = Array.isArray(productData.variants)
          && productData.variants.some(variant => Number(variant.price) > 0);
        if (Number(productData.price) === 0 && !hasPaidVariant) {
          console.log('⏭️ Skipping blind tasting product:', productId, productData.title);
          return null;
        }

        return {
          ...productData,
          score: item.score,
          distance: item.distance,
          wineColor: item.color || productData.wineColor || '' // Preserve API wine color (R/W)
        };
      })
      .filter(item => item !== null)
      .sort((a, b) => b.score - a.score);

    // GARANTIE ABSOLUE : toujours au moins 3 résultats à 70%+ (global)
    const MIN_RESULTS = 3;
    const MIN_SCORE = 70;
    
    // Vérifier si au moins 3 vins sont à 70%+
    let aboveMinScore = allMatched.filter(item => item.score >= MIN_SCORE);
    
    if (aboveMinScore.length < MIN_RESULTS && allMatched.length >= MIN_RESULTS) {
      // Pas assez à 70%+ → recalculer les scores pour que le 3e atteigne 70%
      const thirdBestDistance = allMatched[MIN_RESULTS - 1].distance;
      if (thirdBestDistance > 0) {
        const newMultiplier = (100 - MIN_SCORE) / thirdBestDistance;
        console.log(`📊 Manager: rescaling scores, new multiplier: ${newMultiplier.toFixed(2)} (3rd distance: ${thirdBestDistance.toFixed(3)})`);
        allMatched.forEach(item => {
          let newScore = 100 - (item.distance * newMultiplier);
          item.score = Math.round(Math.max(0, Math.min(100, newScore)));
        });
      }
    }
    
    this.recommendations = allMatched;
    
    // Limiter le nombre de produits affichés UNIQUEMENT pour le quiz (pas wine-assistant ni taste-map)
    // UNIFIED SCORING: quiz distance-based scores are the single source of truth.
    // The manager only filters (catalog match + color balance + cap) — never modifies scores.
    // taste-map mode: V2 sliders send all wines; keep them all for filtering.
    if (this.mode === 'quiz') {
      const profile = JSON.parse(sessionStorage.getItem('vinpop_taste_profile') || '{}');
      const colorPref = profile.colorPreference || 'both';
      
      if (colorPref === 'both') {
        // Équilibrage: top 3 rouges + top 3 blancs → best 5 total
        const reds = allMatched.filter(p => (p.wineColor || '').toUpperCase() === 'R').slice(0, 3);
        const whites = allMatched.filter(p => (p.wineColor || '').toUpperCase() === 'W').slice(0, 3);
        const others = allMatched.filter(p => !['R','W'].includes((p.wineColor || '').toUpperCase()));
        
        const balanced = [
          ...reds,
          ...whites,
          ...others.slice(0, Math.max(0, 5 - reds.length - whites.length))
        ].sort((a, b) => b.score - a.score).slice(0, 5);
        
        console.log(`🍷 Manager balance: ${reds.length} red + ${whites.length} white = ${balanced.length}`);
        this.recommendations = balanced;
      } else {
        // Single color: top 3
        this.recommendations = allMatched.slice(0, 3);
        console.log(`🍷 Capped display to ${this.recommendations.length} products (color: ${colorPref})`);
      }
    } else {
      // Non-quiz mode: keep score filter
      let filtered = allMatched.filter(item => item.score >= MIN_SCORE);
      if (filtered.length < MIN_RESULTS && allMatched.length > 0) {
        filtered = allMatched.slice(0, Math.max(MIN_RESULTS, filtered.length));
        filtered.forEach(item => {
          if (item.score < MIN_SCORE) item.score = MIN_SCORE;
        });
      }
      this.recommendations = filtered;
    }
    
    // ULTIME FILET : si recommendations est vide, montrer TOUT le catalogue Shopify
    if (this.recommendations.length === 0) {
      console.warn('⚠️ No wines matched from API → showing full catalog as fallback');
      if (window.shopCatalog && Object.keys(window.shopCatalog).length > 0) {
        this.recommendations = Object.values(window.shopCatalog)
          .slice(0, 12)
          .map(product => ({ ...product, score: 85, distance: 0 }));
      } else {
        console.error('❌ shopCatalog empty - reloading page to show native collection');
        window.location.href = window.location.pathname;
        return;
      }
    }

    console.log(`🍷 Combined data: ${this.recommendations.length} products (matched from API: ${allMatched.length})`);

    // Afficher les produits
    this.displayProducts(this.recommendations);
    
    // Inject subtitle for wine-assistant mode (source product context)
    this.injectSourceProductSubtitle();

    // Explain the match % right above the grid. Runs after the subtitle: the fallback
    // placement anchors on it.
    this.injectMatchScaleLegend();

    // Mettre à jour le compteur
    this.updateCounter();
    
    // ÉTAPE 6 : Injecter le bouton de filtrage
    this.injectFilterButton();
    
    // ÉTAPE 7 : Créer le drawer de filtres
    this.createFilterDrawer();
    
    // ÉTAPE 8 : PROTECTION ANTI-FILTERABLE
    // The Shapes theme Filterable component observes mutations on #facets-filterable
    // and can re-render (replacing innerHTML). We must protect our custom container.
    this._protectFromFilterable();
    
    // ÉTAPE 9 : PERSISTANCE — save final displayed set (unified quiz scores) to sessionStorage
    // CRITICAL: Use separate storage keys for Quiz vs Assistant to prevent interference
    // Scores are the quiz's original distance-based scores — single source of truth for
    // both product cards and the 3D scatter chart (no rank-based override).
    const storageKey = this.mode === 'assistant' ? 'vinpop_assistant_results' : 'vinpop_last_results';
    const finalResults = this.recommendations.map(r => ({
      wine_id: String(r.id || r.wine_id || r.productId || ''),
      score: r.score,
      distance: r.distance || 0,
      color: r.wineColor || r.color || ''
    }));
    sessionStorage.setItem(storageKey, JSON.stringify(finalResults));
    console.log(`✅ Results saved to sessionStorage (${storageKey}) with unified quiz scores`);

    // ÉTAPE 10 : Dispatch event for 3D taste map visualization
    if (this.mode === 'quiz' || this.mode === 'taste-map') {
      document.dispatchEvent(new CustomEvent('vinpop:results-rendered'));
      console.log('📡 Dispatched vinpop:results-rendered for taste map');
    }
  }

  /**
   * Protect our custom container from the Shapes theme Filterable component
   * Filterable listens for popstate and uses renderMarkup to replace #facets-filterable innerHTML
   * We override its renderPage to no-op when our takeover is active
   */
  _protectFromFilterable() {
    const filterableEl = document.getElementById('facets-filterable');
    if (!filterableEl) return;
    
    // Method 1: Intercept popstate to block Filterable re-renders
    if (!window._vinpopPopstateGuard) {
      window._vinpopPopstateGuard = true;
      window.addEventListener('popstate', (e) => {
        // If we have quiz results displayed, prevent Filterable from nuking them
        const hasOurContainer = document.getElementById('js-wine-results');
        if (hasOurContainer && hasOurContainer.children.length > 0) {
          e.stopImmediatePropagation();
          console.log('🛡️ Blocked Filterable popstate re-render (quiz results protected)');
        }
      }, true); // Use capture phase to fire BEFORE Filterable's listener
    }
    
    console.log('🛡️ Filterable protection activated');
  }

  /**
   * Inject a subtitle below the collection title for wine-assistant mode
   * Shows: "You are likely to enjoy these wines because you selected {wine name}"
   */
  injectSourceProductSubtitle() {
    // Only when source_product is in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const sourceProductId = urlParams.get('source_product') || urlParams.get('main-product');
    if (!sourceProductId) return;
    
    // Don't inject twice
    if (document.getElementById('vinpop-source-subtitle')) return;
    
    // Find source wine name: the product with score=100 in recommendations
    let sourceProductName = null;
    
    // Source 1: recommendations array (most reliable — score 100 = selected wine)
    if (this.recommendations && this.recommendations.length > 0) {
      const selected = this.recommendations.find(p => p.score === 100 || p.score === '100');
      if (selected) sourceProductName = selected.title;
    }
    
    // Source 2: shopCatalog by ID
    if (!sourceProductName && window.shopCatalog) {
      const catalogProduct = window.shopCatalog[String(sourceProductId)];
      if (catalogProduct) sourceProductName = catalogProduct.title;
    }
    
    // Source 3: DOM — find the card with data-score="100" h3
    if (!sourceProductName) {
      const card100 = document.querySelector('[data-score="100"]');
      if (card100) {
        const h3 = card100.querySelector('h3');
        if (h3) sourceProductName = h3.textContent.trim();
      }
    }
    
    // Source 4: sessionStorage (from click on previous page)
    if (!sourceProductName) {
      sourceProductName = sessionStorage.getItem('wineAssistantClickedProductName');
    }
    
    if (!sourceProductName) {
      console.warn('⚠️ Could not find source product name for subtitle');
      return;
    }
    
    const isNL = (document.documentElement.lang || 'en').toLowerCase().startsWith('nl');
    
    // Find the collection title — try multiple selectors
    const titleEl = document.querySelector('.section-content h1') 
                 || document.querySelector('h1.font-heading')
                 || document.querySelector('h1.heading-feature')
                 || document.querySelector('h1');
    
    if (!titleEl) {
      console.warn('⚠️ No h1 title found for subtitle injection');
      return;
    }
    
    const subtitle = document.createElement('p');
    subtitle.id = 'vinpop-source-subtitle';
    subtitle.style.cssText = 'text-align: center; margin-top: 0.75rem; margin-bottom: 0.5rem; font-size: 1rem; opacity: 0.85; line-height: 1.4; max-width: 600px; margin-left: auto; margin-right: auto;';
    subtitle.innerHTML = isNL 
      ? `Je hebt kans deze wijnen lekker te vinden omdat je <strong>${sourceProductName}</strong> hebt geselecteerd`
      : `You are likely to enjoy these wines because you selected <strong>${sourceProductName}</strong>`;
    
    // Insert directly after the h1 element
    if (titleEl.nextSibling) {
      titleEl.parentNode.insertBefore(subtitle, titleEl.nextSibling);
    } else {
      titleEl.parentNode.appendChild(subtitle);
    }
    console.log('✅ Source product subtitle injected:', sourceProductName);
  }

  /**
   * Styles for the match scale legend, injected once.
   *
   * The legend lands inside the theme's feature-text banner, which carries `font-heading`
   * and a heading text scale. Inherited, that turned the scale into a wall of display type
   * that could not be read. Everything here is scoped under `.vinpop-match-legend` (two
   * classes deep) so it outranks the theme's own `.inline-heading-rte p` style rules, and
   * the font reset carries !important because the banner's heading styling is exactly what
   * has to lose.
   *
   * Colours are all derived from `currentColor`, never hardcoded: the banner's colour
   * scheme is set in the theme editor and the legend has to survive it being changed. No
   * yellow anywhere, per the brand rule that reserves it for buttons that take money.
   */
  _injectMatchLegendStyles() {
    if (document.getElementById('vinpop-match-legend-styles')) return;

    const style = document.createElement('style');
    style.id = 'vinpop-match-legend-styles';
    style.textContent = `
      .vinpop-match-legend {
        max-width: 34rem;
        margin: 0 auto;
        text-align: left;
      }
      .vinpop-match-legend,
      .vinpop-match-legend * {
        font-family: var(--font-body-family, system-ui), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
        font-weight: 400 !important;
        font-style: normal;
        letter-spacing: normal;
        text-transform: none;
      }
      /* The banner's own vertical padding is sized for a headline, not for a compact
         reference block, and it was most of the height. Trimmed only on the section we
         took over, via a class we set ourselves rather than :has(), so the rule cannot
         reach any other feature-text on the page. !important because the theme's
         py-section-vertical-spacing utility is what has to lose. */
      .section-content.vinpop-match-legend-host {
        padding-top: 1.1rem !important;
        padding-bottom: 1.1rem !important;
      }
      .vinpop-match-legend .vinpop-match-legend__intro {
        margin: 0 0 0.5rem;
        text-align: center;
        font-size: 0.9rem;
        line-height: 1.4;
        opacity: 0.92;
      }
      .vinpop-match-legend .vinpop-match-legend__list {
        list-style: none;
        margin: 0;
        padding: 0;
        border: 1px solid rgba(128, 128, 128, 0.28);
        border-color: color-mix(in srgb, currentColor 20%, transparent);
        border-radius: 12px;
        overflow: hidden;
      }
      .vinpop-match-legend .vinpop-match-legend__row {
        display: grid;
        grid-template-columns: auto 5.2rem 1fr;
        align-items: center;
        gap: 0.6rem;
        margin: 0;
        padding: 0.38rem 0.8rem;
        font-size: 0.875rem;
        line-height: 1.3;
      }
      .vinpop-match-legend .vinpop-match-legend__row + .vinpop-match-legend__row {
        border-top: 1px solid rgba(128, 128, 128, 0.2);
        border-top-color: color-mix(in srgb, currentColor 14%, transparent);
      }
      .vinpop-match-legend .vinpop-match-legend__dot {
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: currentColor;
      }
      .vinpop-match-legend .vinpop-match-legend__range {
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      .vinpop-match-legend .vinpop-match-legend__label {
        opacity: 0.78;
      }
      /* Mobile keeps the single-line layout: dot, range and label stay on one row.
         An earlier version stacked the label under the range, which is what made every
         entry two lines tall on a phone. Budget at 360px: 22px padding, 8px dot, 19px of
         gaps and a 4.2rem range column leave about 245px for the label, against roughly
         220px for the longest one. Lengthening a label or the range column eats that
         margin and the wrap comes back. */
      @media (max-width: 480px) {
        .vinpop-match-legend .vinpop-match-legend__row {
          grid-template-columns: auto 4.2rem 1fr;
          gap: 0.5rem;
          padding: 0.38rem 0.7rem;
          font-size: 0.78rem;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Wine-assistant: spell out what the match percentage on each bottle means.
   *
   * "87%" on its own is not readable by a customer: percentage of what, out of what.
   * The scale is printed once above the grid, from getAssistantMatchTiers so it always
   * matches the sentence under each bottle.
   *
   * It takes over the "Select a bottle below" banner rather than adding a block of its
   * own: that instruction has already been carried out by the time this runs, so the
   * banner is the one piece of the page that is free, and it is exactly where the
   * customer is looking before scanning the grid.
   */
  injectMatchScaleLegend() {
    if (this.mode !== 'assistant') return;

    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get('source_product') && !urlParams.get('main-product')) return;

    if (document.getElementById('vinpop-match-legend')) return;

    const isNL = (document.documentElement.lang || 'en').toLowerCase().startsWith('nl');
    const tiers = this.getAssistantMatchTiers(isNL);

    this._injectMatchLegendStyles();

    // Ranges are derived from the tier list, never written by hand: each tier owns
    // the interval between its minimum and the next stricter tier.
    // The dot fades tier by tier, so closeness reads before a single word is.
    const rows = tiers.map((tier, i) => {
      // "94%+" rather than "94% and above": the range column has to stay narrow enough
      // for the label to hold one line next to it on a phone, and "+" reads in both
      // languages, so it needs no translation.
      const range = i === 0
        ? `${tier.min}%+`
        : (isNL ? `${tier.min} tot ${tier.max}%` : `${tier.min} to ${tier.max}%`);
      const dotOpacity = (1 - i * 0.22).toFixed(2);
      return `<li class="vinpop-match-legend__row">` +
             `<span class="vinpop-match-legend__dot" style="opacity:${dotOpacity};" aria-hidden="true"></span>` +
             `<span class="vinpop-match-legend__range">${range}</span>` +
             `<span class="vinpop-match-legend__label">${tier.label}</span>` +
             `</li>`;
    }).join('');

    const intro = isNL
      ? '🎯 Het % op elke fles laat zien hoe dicht de smaak bij je selectie ligt.'
      : '🎯 The % on each bottle shows how close its taste is to the one you selected.';

    const legend = document.createElement('div');
    legend.id = 'vinpop-match-legend';
    legend.className = 'vinpop-match-legend';
    legend.innerHTML =
      `<p class="vinpop-match-legend__intro">${intro}</p>` +
      `<ul class="vinpop-match-legend__list">${rows}</ul>`;

    // Reuse the banner. Matched on its text, not its section id: the id is generated by
    // the theme editor and changes if the section is ever re-added. The NL wording comes
    // from Translate & Adapt and is not in the repo, so the match also accepts the 👇 the
    // banner carries in every language, paired with a word from the sentence so another
    // feature-text block on the page cannot be mistaken for it.
    const banner = Array.from(document.querySelectorAll('section .feature-text')).find(el => {
      const txt = (el.textContent || '').toLowerCase();
      if (txt.includes('select a bottle below') || txt.includes('selecteer hieronder')) return true;
      return txt.includes('👇') && /bottle|fles|recommendation|aanbeveling/.test(txt);
    });
    if (banner) {
      banner.innerHTML = legend.outerHTML;
      // Tag the section wrapper so the padding trim applies here and nowhere else.
      const host = banner.closest('.section-content');
      if (host) host.classList.add('vinpop-match-legend-host');
      console.log('✅ Match scale legend injected (replaced the select-a-bottle banner)');
      return;
    }

    // No banner found (text edited in the theme editor, section removed): fall back to
    // the subtitle under the H1, so the scale is never missing entirely.
    const anchor = document.getElementById('vinpop-source-subtitle');
    if (anchor && anchor.parentNode) {
      legend.style.marginTop = '0.75rem';
      anchor.parentNode.insertBefore(legend, anchor.nextSibling);
      console.log('✅ Match scale legend injected (fallback: under the subtitle)');
      return;
    }

    console.warn('⚠️ Could not place the match scale legend: no banner and no subtitle found');
  }

  /**
   * ÉTAPE 4 : Injection du bouton de filtrage (avant la grille)
   */
  injectFilterButton() {
    // Vérifier si le bouton existe déjà
    if (document.getElementById('js-filter-button')) {
      console.log('✅ Filter button already exists');
      return;
    }
    
    console.log('🔧 Injecting filter button...');
    
    // Trouver où insérer (avant notre conteneur)
    const customContainer = document.getElementById('js-wine-results');
    if (!customContainer || !customContainer.parentNode) {
      console.warn('⚠️ Cannot inject filter button - no container');
      return;
    }
    
    // Créer le conteneur du bouton (style natif du thème)
    const buttonWrapper = document.createElement('div');
    buttonWrapper.id = 'js-filter-button';
    buttonWrapper.className = 'flex items-center justify-between gap-5 pb-5 lg:pb-10';
    
    buttonWrapper.innerHTML = `
      <button
        role="button"
        class="push-btn w-1/2 lg:w-auto"
        onclick="wineManager.openDrawer()"
        type="button"
      >
        <span class="push-btn__surface push-btn__surface--secondary pointer-events-none w-full lg:w-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 inline-block mr-2">
            <path fill-rule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z" clip-rule="evenodd" />
          </svg>
          Filter Your Wines
          <span id="js-active-filter-count"></span>
        </span>
      </button>
      
      <!-- Compteur de produits -->
      <div class="text-sm">
        Showing <strong id="quiz-product-counter">0 wines</strong>
      </div>
    `;
    
    // Insérer AVANT la grille
    customContainer.parentNode.insertBefore(buttonWrapper, customContainer);
    
    // S'assurer que wineManager est accessible globalement pour le onclick
    window.wineManager = this;
    
    console.log('✅ Filter button injected');
  }
  
  /**
   * ÉTAPE 5 : Création du drawer STANDALONE (Vanilla JS + CSS)
   */
  createFilterDrawer() {
    // Vérifier si le drawer existe déjà
    if (document.getElementById('vinpop-drawer')) {
      console.log('✅ Filter drawer already exists');
      // Juste mettre à jour le contenu
      const content = document.getElementById('vinpop-drawer-content');
      if (content) this.createFilterControls('vinpop-drawer-content');
      return;
    }
    
    console.log('🔧 Creating standalone filter drawer...');
    
    // ÉTAPE 1 : Injecter le CSS
    this.injectDrawerCSS();
    
    // ÉTAPE 2 : Créer le backdrop
    const backdrop = document.createElement('div');
    backdrop.id = 'vinpop-backdrop';
    backdrop.addEventListener('click', () => this.closeDrawer());
    
    // ÉTAPE 3 : Créer le drawer
    const drawer = document.createElement('div');
    drawer.id = 'vinpop-drawer';
    
    // Header
    const header = document.createElement('div');
    header.id = 'vinpop-drawer-header';
    
    // Récupérer les traductions depuis window.themeStrings si disponibles
    const filterTitle = window.themeStrings?.taste_profile?.filter_drawer?.title || 'Filter Your Wines';
    const closeLabel = window.themeStrings?.taste_profile?.filter_drawer?.close || 'Close';
    
    header.innerHTML = `
      <h2 style="font-size: 20px; font-weight: 600;">${filterTitle}</h2>
      <button class="vinpop-close-btn" onclick="wineManager.closeDrawer()" aria-label="${closeLabel}">
        ×
      </button>
    `;
    
    // Content
    const content = document.createElement('div');
    content.id = 'vinpop-drawer-content';
    
    drawer.appendChild(header);
    drawer.appendChild(content);
    
    // ÉTAPE 4 : Insérer dans le DOM
    document.body.appendChild(backdrop);
    document.body.appendChild(drawer);
    
    console.log('✅ Standalone drawer created');
    
    // ÉTAPE 5 : Générer les filtres
    this.createFilterControls('vinpop-drawer-content');
  }
  
  /**
   * Injection du CSS nécessaire au drawer
   */
  injectDrawerCSS() {
    if (document.getElementById('vinpop-drawer-styles')) {
      return; // Déjà injecté
    }
    
    const style = document.createElement('style');
    style.id = 'vinpop-drawer-styles';
    style.textContent = `
      /* Backdrop */
      #vinpop-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        z-index: 9998;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s;
      }
      #vinpop-backdrop.is-visible {
        opacity: 1;
        pointer-events: auto;
      }
      
      /* Drawer */
      #vinpop-drawer {
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        width: 350px;
        max-width: 90%;
        background: white;
        z-index: 9999;
        transform: translateX(100%);
        transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        box-shadow: -5px 0 25px rgba(0,0,0,0.15);
        display: flex;
        flex-direction: column;
      }
      #vinpop-drawer.is-visible {
        transform: translateX(0);
      }
      
      /* Header */
      #vinpop-drawer-header {
        padding: 20px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      /* Content */
      #vinpop-drawer-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
      }
      
      /* Close button */
      .vinpop-close-btn {
        cursor: pointer;
        font-size: 32px;
        line-height: 1;
        padding: 5px;
        background: none;
        border: none;
        color: #333;
      }
      .vinpop-close-btn:hover {
        color: #000;
      }
      
      /* Accordéons */
      .vinpop-filter-section {
        margin-bottom: 16px;
        border-bottom: 1px solid #eee;
        padding-bottom: 12px;
      }
      .vinpop-filter-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 16px;
        font-weight: 600;
        text-align: left;
      }
      .vinpop-filter-header:hover {
        color: #555;
      }
      .vinpop-filter-arrow {
        transition: transform 0.2s;
      }
      .vinpop-filter-arrow.is-open {
        transform: rotate(180deg);
      }
      .vinpop-filter-content {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }
      .vinpop-filter-content.is-open {
        max-height: 500px;
      }
      
      /* Checkboxes */
      .vinpop-checkbox-item {
        display: flex;
        align-items: center;
        padding: 8px 0;
        cursor: pointer;
      }
      .vinpop-checkbox-item:hover {
        color: #555;
      }
      .vinpop-checkbox-item input {
        margin-right: 10px;
        width: 18px;
        height: 18px;
        cursor: pointer;
      }
      
      /* Clear button */
      #quiz-clear-filters {
        width: 100%;
        padding: 12px;
        background: #f5f5f5;
        border: 1px solid #ddd;
        cursor: pointer;
        margin-top: 20px;
        font-weight: 600;
      }
      #quiz-clear-filters:hover {
        background: #e5e5e5;
      }
      
      /* S'assure que le prix natif est bien caché */
      .card-information .price,
      .price--on-sale .price-item--regular {
        display: none !important;
      }
      
      /* FORCER L'AFFICHAGE DES BOUTONS */
      .product-tile__quick-buy {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
        position: relative !important;
        margin-top: 10px !important;
        z-index: 20;
      }
      .product-tile__quick-buy .push-btn {
        opacity: 1 !important;
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      /* Style basique pour le sélecteur de quantité si le CSS du thème manque */
      .vinpop-qty-wrapper {
        border: 1px solid currentColor;
        border-radius: 999px;
        display: flex;
        align-items: center;
        padding: 0 5px;
      }
      
      /* FORCE L'AFFICHAGE DU QUICK BUY DANS LA GRILLE RECOMMANDÉE */
      #js-wine-results .product-tile__quick-buy {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 50 !important;
        margin-top: 10px !important;
        pointer-events: auto !important;
      }

      #js-wine-results .product-tile__quick-buy button,
      #js-wine-results .product-tile__quick-buy .quantity-selector-wrapper {
        opacity: 1 !important;
        visibility: visible !important;
        cursor: pointer !important;
        pointer-events: auto !important;
      }

      /* Surcharge pour annuler l'effet "disabled" visuel si le bouton est techniquement actif */
      #js-wine-results .product-tile__quick-buy button:not([disabled]) {
        opacity: 1 !important;
        cursor: pointer !important;
        background-color: var(--color-button-background) !important;
        color: var(--color-button-text) !important;
      }
    `;
    
    document.head.appendChild(style);
    console.log('✅ Drawer CSS injected');
  }
  
  /**
   * Ouvrir le drawer
   */
  openDrawer() {
    const drawer = document.getElementById('vinpop-drawer');
    const backdrop = document.getElementById('vinpop-backdrop');
    
    if (drawer && backdrop) {
      drawer.classList.add('is-visible');
      backdrop.classList.add('is-visible');
      document.body.style.overflow = 'hidden'; // Bloquer le scroll
    }
  }
  
  /**
   * Fermer le drawer
   */
  closeDrawer() {
    const drawer = document.getElementById('vinpop-drawer');
    const backdrop = document.getElementById('vinpop-backdrop');
    
    if (drawer && backdrop) {
      drawer.classList.remove('is-visible');
      backdrop.classList.remove('is-visible');
      document.body.style.overflow = ''; // Restaurer le scroll
    }
  }

  /**
   * Crée une carte produit en clonant le template natif et en remplaçant toutes les données
   */
  createProductCard(product) {
    if (!this.templateCard) {
      console.warn('⚠️ No template card available for cloning');
      return null;
    }
    
    const card = this.templateCard.cloneNode(true);

    // Remove stale theme islands from the template clone (they contain old product JSON/x-data).
    card.querySelectorAll('data-island').forEach((island) => {
      const parent = island.parentNode;
      if (!parent) return;
      while (island.firstChild) {
        parent.insertBefore(island.firstChild, island);
      }
      island.remove();
    });

    // Native product cards contain Alpine bindings scoped to their original
    // data-island. Once cloned/rewritten, those bindings can reference missing
    // variables such as currentVariant and spam the console.
    card.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const attrText = `${attr.name} ${attr.value}`;
        if (/currentVariant|currentPrice|currentVariantId/.test(attrText)) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    // CRITIQUE: Forcer l'affichage visible avec styles inline pour surmonter tout CSS qui pourrait cacher
    card.style.display = 'list-item';
    card.style.visibility = 'visible';
    card.style.opacity = '1';
    card.style.transform = 'none';
    card.style.position = 'relative';
    
    // ============ STOCKER LES DONNÉES PRODUIT DANS MAP JavaScript (plus fiable que attributs HTML) ============
    // Créer un ID unique pour cette card
    const uniqueCardId = `vinpop-card-${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    card.setAttribute('data-vinpop-card-id', uniqueCardId);
    
    // Stocker les données dans la Map
    const preferredVariantForAvailability = this.getPreferredVariant(product, { requireAvailable: false });
    const isAvailable = product.available !== undefined
      ? product.available
      : (preferredVariantForAvailability ? preferredVariantForAvailability.available : true);
    this.productDataMap.set(uniqueCardId, {
      score: product.score,
      available: isAvailable,
      title: product.title
    });
    
    // Supprimer les stickers natifs du clone
    const nativeStickers = card.querySelectorAll('[class*="sticker-taste-score"], [class*="sticker__container"], [class*="sticker-text"]');
    nativeStickers.forEach(sticker => {
      const stickerContainer = sticker.closest('[class*="sticker-taste-score"]');
      if (stickerContainer) {
        stickerContainer.remove();
      } else {
        sticker.remove();
      }
    });
    
    // Supprimer le badge "Already Purchased" du template (sera recréé pour le bon produit)
    const alreadyPurchasedBadge = card.querySelector('.badge-already-purchased') || card.querySelector('[class*="already-purchased-sticker"]');
    if (alreadyPurchasedBadge) {
      alreadyPurchasedBadge.remove();
    }
    
    // Recréer le badge si ce produit a été acheté (native sticker style)
    if (this.mode === 'assistant' && this.purchasedProductIds && this.purchasedProductIds.has(String(product.id))) {
      const tileContainer = card.querySelector('.product-tile__container') || card;
      if (tileContainer) {
        const isEN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
        const badgeText = isEN ? 'Already purchased' : 'Reeds gekocht';
        const stickerDiv = document.createElement('div');
        stickerDiv.className = 'badge-already-purchased pointer-events-none absolute z-20 origin-top-left will-change-transform';
        stickerDiv.style.cssText = 'top: 15%; left: 90%; transform: rotate(5deg) translate(-50%, -50%);';
        stickerDiv.innerHTML = `
          <div class="sticker__container rotate-anim relative">
            <div class="relative max-w-none">
              <div class="absolute inset-0" style="clip-path: circle(50%); background: rgb(var(--color-scheme-accent-1)); color: rgb(var(--color-scheme-accent-1-contrast));">
                <div class="sticker__text absolute inset-0 flex items-center justify-center text-center font-heading" style="font-family: var(--heading-font-stack); font-weight: var(--heading-font-weight); text-transform: var(--sticker-text-transform); font-size: calc(var(--font-size-base) * 0.7); line-height: var(--base-line-height); padding: 0.5rem;">
                  <div>${badgeText}</div>
                </div>
              </div>
              <svg class="sticker__width-container relative max-w-none text-scheme-text-contrast" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width: 80px; height: 80px;">
                <circle class="sticker__svg fill-transparent" cx="50" cy="50" r="48" stroke="rgb(var(--color-scheme-text))" stroke-width="0"></circle>
              </svg>
            </div>
          </div>
        `;
        tileContainer.appendChild(stickerDiv);
      }
    }
    
    // Ajouter les data-attributes pour le filtrage
    card.dataset.productId = product.id;
    card.dataset.score = product.score;
    card.dataset.country = product.country || '';
    card.dataset.type = product.wineColor || product.type || '';
    const displayPriceCents = this.getProductDisplayPrice(product);
    card.dataset.price = displayPriceCents;
    
    // Remplacer les images
    const imgs = card.querySelectorAll('img');
    imgs.forEach(img => {
      if (product.image.url) {
        img.src = product.image.url;
        img.alt = product.image.alt || product.title;
        // Gérer srcset si présent
        if (img.hasAttribute('srcset')) {
          img.srcset = product.image.url;
        }
      }
    });
    
    // Remplacer le titre et les liens
    const allLinks = card.querySelectorAll('a[href*="/products/"]');
    allLinks.forEach(link => {
      link.href = product.url; // Ex: /products/wine-handle
    });
    
    // Remplacer le texte du titre
    const titleElement = card.querySelector('h3.font-body, .product-tile__heading, h3, .h3, [class*="heading"]');
    if (titleElement) {
      titleElement.textContent = product.title;
    }

    // Keep inner product containers aligned with the rendered product ID.
    card.querySelectorAll('.product-tile[data-product-id]').forEach((tile) => {
      tile.setAttribute('data-product-id', String(product.id));
    });
    
    // Remplacer le prix
    let priceElement = card.querySelector('[x-html*="currentPrice"]') ||
                       card.querySelector('.price-item--regular') ||
                       card.querySelector('[data-product-price]') ||
                       card.querySelector('.price') ||
                       card.querySelector('[class*="price__"]');
    
    if (priceElement) {
      // Formater le prix avec Intl.NumberFormat (ex: 7.5 -> €7,50)
      const priceInEuros = displayPriceCents / 100;
      const formattedPrice = new Intl.NumberFormat('fr-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(priceInEuros);
      
      priceElement.textContent = formattedPrice;
      
      // Enlever les attributs Alpine pour éviter les conflits
      priceElement.removeAttribute('x-html');
      priceElement.removeAttribute('x-text');
    }
    
    // ============ JUSTIFICATION TEXT (Quiz + Wine Assistant) ============
    {
      let justification = null;
      if (this.mode === 'quiz' || this.mode === 'taste-map') {
        justification = this.generateJustification(product);
      } else {
        // Wine Assistant: generate justification based on similarity to source wine
        justification = this.generateAssistantJustification(product);
      }
      if (justification) {
        const justifEl = document.createElement('div');
        justifEl.className = 'vinpop-quiz-justification';
        justifEl.innerHTML = justification;
        
        // Insert after the price, inside the text area (product-tile.mt-5 > a)
        const textLink = card.querySelector('.product-tile.mt-5 > a.block, .product-tile.mt-5 > a');
        if (textLink && priceElement) {
          priceElement.insertAdjacentElement('afterend', justifEl);
        } else {
          // Fallback: insert after the product-tile.mt-5 div
          const textArea = card.querySelector('.product-tile.mt-5');
          if (textArea) {
            textArea.appendChild(justifEl);
          }
        }
      }
    }
    
    // Gérer le bouton Add to Cart / Sold Out (bilingual)
    const isNL = (document.documentElement.lang || '').toLowerCase().startsWith('nl');
    const addToCartText = isNL ? 'In Winkelwagen' : 'Add to Cart';
    const soldOutText = isNL ? 'Uitverkocht' : 'Sold Out';
    const button = card.querySelector('button[type="submit"], button[data-product-form], .product-form__submit');
    if (button) {
      const buttonText = button.querySelector('.push-btn__surface, span, .btn__content');
      
      if (buttonText) buttonText.removeAttribute('x-text');
      if (!product.available) {
        button.disabled = true;
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        button.classList.add('cursor-not-allowed', 'disabled');
        
        if (buttonText) buttonText.textContent = soldOutText;
      } else {
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
        button.classList.remove('cursor-not-allowed', 'disabled');
        
        if (buttonText) buttonText.textContent = addToCartText;
      }
      
      // Corriger le span visually-hidden avec le bon titre de produit
      const visuallyHidden = button.querySelector('.visually-hidden');
      if (visuallyHidden) {
        visuallyHidden.textContent = `, ${product.title}`;
      }
    }
    
    // Les badges de score seront créés après Alpine.js (voir setTimeout dans displayProducts)
    
    // Injection du Quick Buy (stratégie: supprimer l'ancien, injecter du neuf)
    const oldQuickBuy = card.querySelector('.product-tile__quick-buy');
    if (oldQuickBuy) {
      oldQuickBuy.remove();
    }
    
    // 2. Récupérer le variant et la disponibilité
    const preferredVariant = this.getPreferredVariant(product, { requireAvailable: false });
    if (!preferredVariant) {
      console.warn('⚠️ No variant found for product:', product.title);
      return card;
    }
    
    // Mise à jour de isAvailable avec le variant préféré
    const variantAvailable = preferredVariant.available !== undefined ? preferredVariant.available : product.available;
    
    // Injecter le nouveau Quick Buy
    const quickBuyHTML = `
      <div class="product-tile__quick-buy" style="display: block !important; opacity: 1 !important; visibility: visible !important; margin-top: 1.25rem !important;">
        <div class="product-quick-buy__container relative z-20 max-w-xs mx-auto">
          <form method="post" action="/cart/add" class="vinpop-ajax-form" data-product-id="${product.id}">
            <input type="hidden" name="id" value="${preferredVariant.id}">
            <input type="hidden" name="quantity" value="1" id="qty-${product.id}">
            
            <div class="flex items-center justify-end gap-2">
              
              <!-- Quantity Selector avec couleurs RGB du thème -->
              <div style="display: flex; align-items: center; border: 2px solid rgb(0, 0, 0) !important; border-radius: 999px; padding: 2px 4px; background: #ffffff !important;">
                <button type="button" 
                        onclick="
                          const input = document.getElementById('qty-${product.id}');
                          const display = document.getElementById('qty-display-${product.id}');
                          const newQty = Math.max(1, parseInt(input.value) - 1);
                          input.value = newQty;
                          display.value = newQty;
                        " 
                        style="padding: 2px 6px; font-size: 0.85rem; cursor: pointer; color: rgb(0, 0, 0) !important; background: transparent !important; border: none !important; line-height: 1; font-weight: 900;">-</button>
                
                <input type="number" 
                       id="qty-display-${product.id}" 
                       value="1" 
                       min="1" 
                       readonly
                       style="width: 24px; text-align: center; border: none !important; background: transparent !important; padding: 0; color: rgb(0, 0, 0) !important; -webkit-appearance: none; -moz-appearance: textfield; font-size: 11px; font-weight: 700;">
                
                <button type="button" 
                        onclick="
                          const input = document.getElementById('qty-${product.id}');
                          const display = document.getElementById('qty-display-${product.id}');
                          const newQty = parseInt(input.value) + 1;
                          input.value = newQty;
                          display.value = newQty;
                        " 
                        style="padding: 2px 6px; font-size: 0.85rem; cursor: pointer; color: rgb(0, 0, 0) !important; background: transparent !important; border: none !important; line-height: 1; font-weight: 900;">+</button>
              </div>
              
              <!-- Add to Cart Button -->
              <button type="submit" 
                      ${variantAvailable ? '' : 'disabled'}
                      style="flex: 1; padding: 12px 24px; background-color: ${variantAvailable ? '#FFCC37' : 'transparent'} !important; color: ${variantAvailable ? '#000000' : 'rgb(0, 0, 0)'} !important; border-radius: 999px; font-weight: 900; cursor: ${variantAvailable ? 'pointer' : 'not-allowed'}; border: 2px solid rgb(0, 0, 0) !important; font-size: 14px; text-transform: uppercase; opacity: ${variantAvailable ? '1' : '0.5'} !important; font-family: 'Prompt', sans-serif; box-shadow: 0 4px 0 rgb(0, 0, 0) !important; position: relative; transition: all 0.1s;">
                ${variantAvailable ? ((document.documentElement.lang || '').toLowerCase().startsWith('nl') ? 'In Winkelwagen' : 'Add to Cart') : ((document.documentElement.lang || '').toLowerCase().startsWith('nl') ? 'Uitverkocht' : 'Sold Out')}
              </button>
              
            </div>
            
            <!-- Delivery Reassurance -->
            <div class="vinpop-delivery-reassurance" style="margin-top: 1rem; padding: 0.75rem; background: linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.15) 100%); border-radius: 12px; border: 1px solid rgba(76, 175, 80, 0.2);">
              <div style="display: flex; align-items: center; gap: 0.5rem; justify-content: center;">
                <span style="font-size: 1.1rem;">🇳🇱</span>
                <span style="font-size: 0.8rem; font-weight: 600; color: rgb(var(--color-scheme-text)); line-height: 1.3; text-align: center;">
                  ${(document.documentElement.lang || '').toLowerCase().startsWith('nl') ? 'Op voorraad in Nederland.<br><span style="font-weight: 400; opacity: 0.85;">Binnen 72 uur bij je thuis via Bulpe Fulfilment 🚚</span>' : 'In stock in the Netherlands.<br><span style="font-weight: 400; opacity: 0.85;">Delivered within 72 hours via Bulpe Fulfilment 🚚</span>'}
                </span>
              </div>
            </div>
            
          </form>
        </div>
      </div>
    `;
    
    // 5. INJECTER après le container
    const quickBuyContainer = card.querySelector('.product-tile__container');
    if (quickBuyContainer) {
      quickBuyContainer.insertAdjacentHTML('afterend', quickBuyHTML);
    }
    
    // 5b. Supprimer le delivery reassurance du template cloné (le quickBuyHTML en contient un nouveau)
    card.querySelectorAll('.vinpop-delivery-reassurance').forEach((el, i) => {
      // Supprimer l'original du template, garder celui injecté par quickBuyHTML
      if (i === 0) el.remove();
    });
    
    // 6. CRÉER ET INJECTER LES BADGES (structure SVG identique aux badges natifs du site)
    const mainLink = card.querySelector('a[href*="/products/"]');
    if (mainLink) {
      mainLink.style.position = 'relative';
      
      // Special case: if score is 100, it's the selected product
      const isSelectedProduct = product.score === 100;
      
      // Check if custom recommendation text exists
      const hasCustomText = product.recommendation_text && typeof product.recommendation_text === 'string' && product.recommendation_text.trim().length > 0;
      
      // For selected product (100%), always show "Selected wine" text with larger styling
      const badgeContent = isSelectedProduct 
        ? `<span style="font-size: var(--font-size-base); font-weight: 700; line-height: 1.1; text-align: center;">Selected<br>wine</span>`
        : (hasCustomText 
          ? `<span style="font-size: 0.65rem; line-height: 1.2;">${product.recommendation_text}</span>`
          : `<div>${product.score}%</div>`);
      
      // Badge SCORE - all badges are circular (yellow background)
      const scoreBadgeHTML = `
        <div class="vinpop-score-badge pointer-events-none absolute z-20 origin-top-left will-change-transform" 
             style="top: 15%; right: 10%; transform: rotate(5deg) translate(50%, -50%);"
             ${isSelectedProduct || hasCustomText ? 'title="' + (isSelectedProduct ? 'Selected wine' : product.recommendation_text) + '"' : ''}>
          <div class="sticker__container rotate-anim relative">
            <div class="relative max-w-none">
              <div class="absolute inset-0" style="clip-path: circle(50%); background: rgb(var(--color-scheme-accent-2)); color: rgb(var(--color-scheme-accent-2-contrast));">
                <div class="sticker__text absolute inset-0 flex items-center justify-center text-center font-heading" style="font-family: var(--heading-font-stack); font-weight: var(--heading-font-weight); text-transform: ${isSelectedProduct || hasCustomText ? 'none' : 'var(--sticker-text-transform)'}; font-size: var(--font-size-base); line-height: var(--base-line-height);">
                  ${badgeContent}
                </div>
              </div>
              <svg class="sticker__width-container relative max-w-none text-scheme-text-contrast" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width: 80px; height: 80px;">
                <circle class="sticker__svg fill-transparent" cx="50" cy="50" r="48" stroke="rgb(var(--color-scheme-text))" stroke-width="0"></circle>
              </svg>
            </div>
          </div>
        </div>
      `;
      
      let badgesHTML = scoreBadgeHTML;
      
      // Badge SOLD OUT (structure SVG native avec rotation)
      if (!isAvailable) {
        badgesHTML += `
          <div class="vinpop-soldout-badge pointer-events-none absolute z-20 origin-top-left will-change-transform" style="top: 15%; left: 10%; transform: rotate(-5deg) translate(-50%, -50%);">
            <div class="sticker__container rotate-anim relative">
              <div class="relative max-w-none">
                <div class="absolute inset-0 bg-scheme-text text-scheme-text-contrast" style="clip-path: circle(50%);">
                  <div class="sticker__text absolute inset-0 flex items-center justify-center text-center font-heading" style="font-family: var(--heading-font-stack); font-weight: var(--heading-font-weight); text-transform: var(--sticker-text-transform); font-size: var(--font-size-base); line-height: var(--base-line-height); padding: 0 1rem;">
                    <div>Sold Out</div>
                  </div>
                </div>
                <svg class="sticker__width-container relative max-w-none text-scheme-text-contrast" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" style="width: 100px; height: 100px;">
                  <circle class="sticker__svg fill-transparent" cx="60" cy="60" r="58" stroke="rgb(var(--color-scheme-text))" stroke-width="0"></circle>
                </svg>
              </div>
            </div>
          </div>
        `;
      }
      
      mainLink.insertAdjacentHTML('beforeend', badgesHTML);
    }
    
    return card;
  }

  /**
   * Generate a short justification text explaining why this wine matches the quiz answers
   * Based on the user's ACTUAL quiz choices, not wine metadata
   */
  generateJustification(product) {
    try {
      const profileJson = sessionStorage.getItem('vinpop_taste_profile');
      if (!profileJson) return null;
      
      const profile = JSON.parse(profileJson);
      const lang = (document.documentElement.lang || 'en').toLowerCase().substring(0, 2);
      const isNL = lang === 'nl';
      const choices = profile.choices || {};
      const reasons = [];
      
      // Wine color from the quiz data (R/W from API)
      const wineColor = (product.wineColor || product.type || product.color || '').toUpperCase();
      const isRed = wineColor.includes('R') || wineColor.includes('RED') || wineColor.includes('ROOD');
      const isWhite = wineColor.includes('W') || wineColor.includes('WHITE') || wineColor.includes('WIT');
      
      // ── Q1: Coffee → tannin/bitterness ──
      // A=Black coffee (bold), B=Cappuccino (mild), C=Tea (light)
      if (choices.q1 === 0) {
        // Client likes it strong & bitter
        if (isRed) {
          reasons.push(isNL 
            ? '☕ Je houdt van zwarte koffie → deze rode wijn heeft dezelfde kracht'
            : '☕ You like black coffee → this red has that same boldness');
        } else {
          reasons.push(isNL
            ? '☕ Je houdt van intense smaken → deze wijn heeft karakter'
            : '☕ You enjoy intense flavors → this wine has character');
        }
      } else if (choices.q1 === 1) {
        // Client likes it creamy & mild
        reasons.push(isNL
          ? '☕ Je kiest voor mild & romig → deze wijn is zacht en toegankelijk'
          : '☕ You prefer mild & creamy → this wine is smooth and approachable');
      } else if (choices.q1 === 2) {
        // Client prefers light (tea/water)
        reasons.push(isNL
          ? '☕ Je houdt van lichte dranken → deze wijn is fris en delicaat'
          : '☕ You enjoy light drinks → this wine is fresh and delicate');
      }
      
      // ── Q2: Chocolate → sweetness vs structure ──
      // A=Milk/White choco (sweet), B=Dark >70% (intense), C=Fruit jellies (fruity)
      if (choices.q2 === 0) {
        reasons.push(isNL
          ? '🍫 Je kiest voor zoete chocolade → fruitige, zachte smaken passen bij jou'
          : '🍫 You choose sweet chocolate → fruity, soft flavors suit you');
      } else if (choices.q2 === 1) {
        reasons.push(isNL
          ? '🍫 Je kiest voor pure chocolade → complexe, gestructureerde wijnen passen bij jou'
          : '🍫 You choose dark chocolate → complex, structured wines suit you');
      } else if (choices.q2 === 2) {
        reasons.push(isNL
          ? '🍫 Je houdt van fruitige smaken → deze wijn heeft mooie fruittonen'
          : '🍫 You love fruity flavors → this wine has beautiful fruit notes');
      }
      
      // ── Q3: Dressing → acidity ──
      // A=Lemon/vinaigrette (acidic), B=Creamy/mayo (rich), C=Olive oil (balanced)
      if (choices.q3 === 0) {
        reasons.push(isNL
          ? '🥗 Je houdt van citroen & azijn → de frisse zuren in deze wijn zijn voor jou'
          : '🥗 You love lemon & vinegar → the crisp acidity in this wine is for you');
      } else if (choices.q3 === 1) {
        reasons.push(isNL
          ? '🥗 Je kiest voor romige dressings → deze wijn is rond en vol in de mond'
          : '🥗 You prefer creamy dressings → this wine is round and full-bodied');
      } else if (choices.q3 === 2) {
        reasons.push(isNL
          ? '🥗 Je houdt het graag simpel → deze wijn is puur en gebalanceerd'
          : '🥗 You prefer simplicity → this wine is pure and balanced');
      }
      
      // ── Q4: Cheese → complexity ──
      // A=Brie/Mozzarella (mild), B=Aged Parmesan/Gouda (complex)
      if (choices.q4 === 0) {
        reasons.push(isNL
          ? '🧀 Je kiest voor zachte kazen → deze wijn is soepel en verfijnd'
          : '🧀 You prefer mild cheeses → this wine is supple and refined');
      } else if (choices.q4 === 1) {
        reasons.push(isNL
          ? '🧀 Je houdt van belegen kaas → deze wijn heeft diezelfde diepgang'
          : '🧀 You love aged cheese → this wine has that same depth');
      }
      
      // ── Q5: Mood → final calibration ──
      // A=Fresh & Fruity, B=Smooth & Round, C=Bold & Powerful
      if (choices.q5 === 0) {
        reasons.push(isNL
          ? '🎯 Je zocht iets fris & fruitig → deze wijn is precies dat'
          : '🎯 You wanted something fresh & fruity → this wine is exactly that');
      } else if (choices.q5 === 1) {
        reasons.push(isNL
          ? '🎯 Je zocht iets zacht & rond → deze wijn heeft die elegantie'
          : '🎯 You wanted something smooth & round → this wine has that elegance');
      } else if (choices.q5 === 2) {
        reasons.push(isNL
          ? '🎯 Je zocht iets krachtig & intens → deze wijn is precies dat'
          : '🎯 You wanted something bold & powerful → this wine is exactly that');
      }
      
      // Select 2 most relevant reasons: prioritize by wine color relevance
      // For RED: Q1 (tannin) + Q4 (complexity) or Q5 (mood) are most relevant
      // For WHITE: Q3 (acidity) + Q2 (sweetness) or Q5 (mood) are most relevant
      // Fallback for any color
      let selected = [];
      if (isRed && reasons.length > 0) {
        // Prefer Q1, Q4, Q5, then Q2, Q3
        const priority = [0, 3, 4, 1, 2]; // indices into reasons array (Q1,Q4,Q5,Q2,Q3)
        for (const idx of priority) {
          if (reasons[idx] && selected.length < 2) selected.push(reasons[idx]);
        }
      } else if (isWhite && reasons.length > 0) {
        // Prefer Q3, Q2, Q5, then Q1, Q4
        const priority = [2, 1, 4, 0, 3]; // Q3,Q2,Q5,Q1,Q4
        for (const idx of priority) {
          if (reasons[idx] && selected.length < 2) selected.push(reasons[idx]);
        }
      }
      
      // Fallback: just take first 2
      if (selected.length < 2) {
        selected = reasons.slice(0, 2);
      }
      
      if (selected.length === 0) {
        // Ultimate fallback based on score
        if (product.score >= 95) {
          selected.push(isNL ? '🎯 Perfecte match voor jouw smaakprofiel' : '🎯 Perfect match for your taste profile');
        } else if (product.score >= 90) {
          selected.push(isNL ? '🎯 Uitstekende match voor jouw smaak' : '🎯 Excellent match for your palate');
        } else {
          selected.push(isNL ? '🎯 Goede match op basis van jouw antwoorden' : '🎯 Great match based on your answers');
        }
      }
      
      return selected.map(r => `<span class="vinpop-justif-line">${r}</span>`).join('');
      
    } catch (e) {
      console.warn('⚠️ Could not generate justification:', e);
      return null;
    }
  }

  /**
   * Wine-assistant: what each match percentage actually means, ordered high to low.
   *
   * Single source of truth. The per-bottle justification and the legend above the
   * grid both read this table, so the scale the customer is shown can never end up
   * contradicting the badge printed on a bottle.
   *
  * `showSource` says whether the line names the wine the customer selected. We keep
  * that only for the two closest bands; below that, the copy is about exploration
  * and does not imply direct similarity to the selected bottle.
   *
   * Assistant mode drops everything under MIN_SCORE (70), so 70 is the real floor a
   * customer can see. The sub-70 wording lives in the caller, as a safety net only.
   */
  getAssistantMatchTiers(isNL) {
    // Keep Smart Wine Box thresholds as the source for the top three bands:
    // love (94+), like (88-93), adventure (84-87).
    // Assistant keeps one extra exploratory band (70-83) instead of hiding those wines.
    return [
      { min: 94, max: 100, showSource: true, label: isNL ? 'hier ga je van houden' : 'you will love it' },
      { min: 88, max: 93, showSource: true, label: isNL ? 'deze ga je lekker vinden' : 'you will like it' },
      { min: 84, max: 87, showSource: false, label: isNL ? 'nieuw avontuur binnen je smaak' : 'a safe adventure in your taste zone' },
      { min: 70, max: 83, showSource: false, label: isNL ? 'ontdek iets nieuws' : 'discover something new' }
    ];
  }

  /**
   * Generate justification text for wine-assistant mode
   * Based on similarity to the source wine (the one the customer selected)
   */
  generateAssistantJustification(product) {
    try {
      const isNL = (document.documentElement.lang || 'en').toLowerCase().startsWith('nl');
      const score = parseInt(product.score) || 0;

      // Selected wine (score=100): show "Selected wine" text
      if (score === 100) {
        const text = isNL ? '🍷 Geselecteerde wijn' : '🍷 Selected wine';
        return `<span class="vinpop-justif-line">${text}</span>`;
      }

      // Find the source wine (score=100) to reference its name
      let sourceName = null;
      if (this.recommendations) {
        const sourceWine = this.recommendations.find(p => p.score === 100 || p.score === '100');
        if (sourceWine) sourceName = sourceWine.title;
      }

      const tier = this.getAssistantMatchTiers(isNL).find(t => score >= t.min) || {
        showSource: false,
        label: isNL ? 'ontdek iets nieuws' : 'discover something new'
      };

      const named = tier.showSource && sourceName ? (isNL ? ' met ' : ' with ') + sourceName : '';
      // Colon, not an em dash: em dashes are banned in customer-facing copy.
      const text = `🎯 ${score}% match${named} : ${tier.label}`;

      return `<span class="vinpop-justif-line">${text}</span>`;
    } catch (e) {
      console.warn('⚠️ Could not generate assistant justification:', e);
      return null;
    }
  }

  /**
   * Affiche une liste de produits dans le conteneur
   * Utilise le clonage pour préserver le style natif
   */
  displayProducts(products) {
    if (!this.container) return;
    
    // Vider le conteneur
    this.container.innerHTML = '';
    
    // Remove any existing starter pack styles
    const existingStyle = document.getElementById('vinpop-starter-pack-styles');
    if (existingStyle) existingStyle.remove();
    
    // Inject Starter Pack CSS once
    if (!document.getElementById('vinpop-starter-pack-styles')) {
      const style = document.createElement('style');
      style.id = 'vinpop-starter-pack-styles';
      style.textContent = `
        .vinpop-starter-pack-wrapper {
          grid-column: 1 / -1;
          position: relative;
          pointer-events: none;
          z-index: 0;
        }
        .vinpop-starter-pack__badge {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          background: rgb(var(--color-scheme-accent-2));
          color: rgb(var(--color-scheme-accent-2-contrast));
          font-weight: 700;
          font-size: 0.85rem;
          letter-spacing: 0.02em;
          padding: 0.4rem 1.25rem;
          border-radius: 9999px;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(var(--color-scheme-accent-2), 0.3);
          z-index: 3;
        }
        @media (max-width: 989px) {
          .vinpop-starter-pack__badge {
            font-size: 0.75rem;
            padding: 0.35rem 1rem;
            top: -12px;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    const isEN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
    const isQuizMode = this.mode === 'quiz' || this.mode === 'taste-map';
    
    // Separate top 3 for starter pack (only in REAL quiz mode, NOT demo, with 3+ products)
    const isRealQuizMode = isQuizMode && !window.__vinpopQuizDemoMode;
    if (isRealQuizMode && products.length >= 3) {
      // Add ALL products to the grid normally (same layout as before)
      products.forEach(product => {
        const card = this.createProductCard(product);
        if (card) this.container.appendChild(card);
      });
      
      // After rendering, visually wrap the first 3 cards with a background overlay
      // We use requestAnimationFrame to ensure cards are laid out
      requestAnimationFrame(() => {
        const cards = this.container.children;
        if (cards.length < 3) return;
        
        // Create the wrapper overlay positioned behind the first 3 cards
        const wrapper = document.createElement('div');
        wrapper.className = 'vinpop-starter-pack-wrapper';
        wrapper.setAttribute('aria-hidden', 'true');
        
        // Badge
        const badge = document.createElement('div');
        badge.className = 'vinpop-starter-pack__badge';
        badge.textContent = isEN ? '🍷 This is your ideal Starter Pack' : '🍷 Dit is jouw ideale Starter Pack';
        wrapper.appendChild(badge);
        
        // Insert the wrapper into the grid container, then move the first 3 cards inside
        this.container.insertBefore(wrapper, cards[0]);
        
        // Position the wrapper using JS to cover the first 3 cards
        // Use ResizeObserver for dynamic sizing
        const updateWrapperPosition = () => {
          const containerCards = Array.from(this.container.children).filter(el => !el.classList.contains('vinpop-starter-pack-wrapper'));
          if (containerCards.length < 3) return;
          
          const containerRect = this.container.getBoundingClientRect();
          const rects = [0, 1, 2].map(i => containerCards[i].getBoundingClientRect());
          const pad = 12;
          const badgeH = 28;
          
          // Check if cards are on multiple rows (mobile 2-col layout)
          const row1Top = rects[0].top;
          const isMultiRow = Math.abs(rects[2].top - row1Top) > 20; // card 3 is on a different row
          
          if (isMultiRow) {
            // L-SHAPE: row 1 has card1+card2 (full width), row 2 has card3 only (left half)
            // Use two divs to form the L
            
            // Remove old inner divs if any
            wrapper.querySelectorAll('.sp-row').forEach(el => el.remove());
            wrapper.style.cssText = 'position:absolute; pointer-events:none; z-index:0;';
            
            // Row 1 box: covers card 1 and card 2
            const r1Left = Math.min(rects[0].left, rects[1].left) - containerRect.left;
            const r1Right = Math.max(rects[0].right, rects[1].right) - containerRect.left;
            const r1Top = Math.min(rects[0].top, rects[1].top) - containerRect.top;
            const r1Bottom = Math.max(rects[0].bottom, rects[1].bottom) - containerRect.top;
            
            // Row 2 box: covers card 3 only
            const r2Left = rects[2].left - containerRect.left;
            const r2Right = rects[2].right - containerRect.left;
            const r2Top = rects[2].top - containerRect.top;
            const r2Bottom = rects[2].bottom - containerRect.top;
            
            // Overall bounding box for the wrapper element
            const wTop = r1Top - pad;
            const wLeft = Math.min(r1Left, r2Left) - pad;
            const wRight = r1Right + pad;
            const wBottom = r2Bottom + pad;
            
            wrapper.style.top = wTop + 'px';
            wrapper.style.left = wLeft + 'px';
            wrapper.style.width = (wRight - wLeft) + 'px';
            wrapper.style.height = (wBottom - wTop) + 'px';
            wrapper.style.overflow = 'visible';
            
            // Row 1 rectangle (full width of wrapper)
            const row1Div = document.createElement('div');
            row1Div.className = 'sp-row';
            row1Div.style.cssText = `
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: ${(r1Bottom + pad) - wTop}px;
              background: #81B29A;
              border: 2px solid rgb(var(--color-scheme-text, 0, 0, 0));
              border-radius: 16px 16px 0 16px;
              pointer-events: none;
            `;
            wrapper.appendChild(row1Div);
            
            // Row 2 rectangle (left portion only, connected to row 1)
            const row2Div = document.createElement('div');
            row2Div.className = 'sp-row';
            const row2WidthPx = (r2Right + pad) - wLeft;
            row2Div.style.cssText = `
              position: absolute;
              top: ${(r1Bottom + pad) - wTop - 2}px;
              left: 0;
              width: ${row2WidthPx}px;
              height: ${(r2Bottom + pad) - (r1Bottom + pad) + 2}px;
              background: #81B29A;
              border: 2px solid rgb(var(--color-scheme-text, 0, 0, 0));
              border-top: none;
              border-radius: 0 0 16px 16px;
              pointer-events: none;
            `;
            wrapper.appendChild(row2Div);
            
            // Badge sits on top edge of the row1 box
            const badge = wrapper.querySelector('.vinpop-starter-pack__badge');
            if (badge) {
              badge.style.position = 'absolute';
              badge.style.top = '-14px';
              badge.style.left = '50%';
              badge.style.transform = 'translateX(-50%)';
              badge.style.zIndex = '3';
            }
            
          } else {
            // DESKTOP: simple rectangle covering all 3 cards on one row
            wrapper.querySelectorAll('.sp-row').forEach(el => el.remove());
            
            const minTop = Math.min(...rects.map(r => r.top));
            const maxBottom = Math.max(...rects.map(r => r.bottom));
            const minLeft = Math.min(...rects.map(r => r.left));
            const maxRight = Math.max(...rects.map(r => r.right));
            
            const top = minTop - containerRect.top;
            const left = minLeft - containerRect.left;
            const right = maxRight - containerRect.left;
            const bottom = maxBottom - containerRect.top;
            
            wrapper.style.cssText = `
              position: absolute;
              top: ${top - pad}px;
              left: ${left - pad}px;
              width: ${right - left + pad * 2}px;
              height: ${bottom - top + pad * 2}px;
              overflow: visible;
              pointer-events: none;
              z-index: 0;
              background: #81B29A;
              border: 2px solid rgb(var(--color-scheme-text, 0, 0, 0));
              border-radius: 16px;
            `;
            
            const badge = wrapper.querySelector('.vinpop-starter-pack__badge');
            if (badge) {
              badge.style.position = 'absolute';
              badge.style.top = '-14px';
              badge.style.left = '50%';
              badge.style.transform = 'translateX(-50%)';
              badge.style.zIndex = '3';
            }
          }
        };
        
        // Make the container position:relative for absolute positioning
        this.container.style.position = 'relative';
        
        // Give cards a higher z-index so they're above the wrapper
        Array.from(this.container.children).forEach(el => {
          if (!el.classList.contains('vinpop-starter-pack-wrapper')) {
            el.style.position = 'relative';
            el.style.zIndex = '1';
          }
        });
        
        updateWrapperPosition();
        // Update on resize
        window.addEventListener('resize', updateWrapperPosition);
        // Update after images load
        setTimeout(updateWrapperPosition, 500);
        setTimeout(updateWrapperPosition, 1500);
      });
    } else {
      // Non-quiz mode or fewer than 3 products: normal display
      products.forEach(product => {
        const card = this.createProductCard(product);
        if (card) this.container.appendChild(card);
      });
    }
    
    console.log(`✅ Displayed ${products.length} cloned product cards${isQuizMode && products.length >= 3 ? ' (top 3 in Starter Pack)' : ''}`);
    
    // 🔧 CRITIQUE: Réinitialiser Alpine.js après que TOUTES les cartes soient dans le DOM
    // On doit détruire puis recréer les composants Alpine.js
    if (window.Alpine) {
      const container = this.container; // Capturer le contexte avant setTimeout
      const productDataMap = this.productDataMap; // Capturer la Map avant setTimeout
      
      // Éviter la double réinitialisation si déjà en cours
      if (this._alpineReinitTimeout) {
        clearTimeout(this._alpineReinitTimeout);
      }
      
      this._alpineReinitTimeout = setTimeout(() => {
        let alpineCount = 0;
        let islandCount = 0;
        
        console.log('🔧 Starting Alpine.js reinitialization...');
        
        // ÉTAPE 1: Détruire les anciens composants Alpine.js
        container.querySelectorAll('[x-data]').forEach(el => {
          // Supprimer les références Alpine.js du template cloné
          if (el._x_dataStack) {
            delete el._x_dataStack;
            delete el._x_bindings;
            delete el._x_refs;
            delete el._x_effects;
            delete el._x_cleanups;
          }
        });
        
        // ÉTAPE 2: Réinitialiser tous les composants Alpine.js
        container.querySelectorAll('[x-data]').forEach(el => {
          try {
            window.Alpine.initTree(el);
            alpineCount++;
          } catch (e) {
            console.warn('  ⚠️ Failed to reinit Alpine.js:', e);
          }
        });
        
        // ÉTAPE 3: Réinitialiser tous les data-island
        container.querySelectorAll('data-island').forEach(island => {
          if (!island.shadowRoot && island.connectedCallback) {
            try {
              island.connectedCallback();
              islandCount++;
            } catch (e) {
              console.warn('  ⚠️ Failed to hydrate data-island:', e);
            }
          }
        });
        
        console.log(`✅ Reinitialization complete: ${alpineCount} Alpine.js components, ${islandCount} data-islands`);
        
        // NOTE: Les badges sont maintenant créés directement dans createProductCard()
        // Plus besoin de les créer ici après Alpine.js
        
        // ÉTAPE 4: Attacher les gestionnaires AJAX aux formulaires
        this.attachAjaxFormHandlers();
        
      }, 200); // 200ms pour laisser le DOM se stabiliser complètement
    }

    // Inject sticky bottom banner for Top 3 (real quiz only, NOT demo mode, 3+ products)
    const isRealQuiz = isQuizMode && !window.__vinpopQuizDemoMode;

    // Add combined video + text CTA card into the product grid
    // Disabled on quizz page — V2 taste map replaces the quiz CTA
    if (false && isQuizMode) {
      const videoSection = document.querySelector('[id*="video_with_text_3kWhjK"]');
      const isNL = (document.documentElement.lang || '').toLowerCase().startsWith('nl');
      const ctaTitle = isNL ? 'De Quiz was pas stap één.' : 'The Quiz was just step one.';
      const ctaSubtitle = isNL
        ? 'Bestel deze Top 3 om je smaak te kalibreren. Na beoordeling ontgrendel je jouw Wine Playlist met wiskundig gematchte wijnen.'
        : 'Order this Top 3 to calibrate your palate. Once rated, unlock your Wine Playlist with mathematically matched wines.';

      const videoEl = videoSection?.querySelector('video');
      const videoSrc = videoEl?.querySelector('source')?.getAttribute('src') || videoEl?.getAttribute('src') || '';

      const comboCard = document.createElement('li');
      comboCard.className = 'vinpop-combo-card';
      comboCard.innerHTML = `
        <div class="vinpop-combo-card__inner">
          ${videoSrc ? `<div class="vinpop-combo-card__video-wrap"><video autoplay loop muted playsinline class="vinpop-combo-card__video">
            <source src="${videoSrc}" type="video/mp4">
          </video></div>` : ''}
          <div class="vinpop-combo-card__text">
            <span class="vinpop-combo-card__title">${ctaTitle}</span>
            <span class="vinpop-combo-card__subtitle">${ctaSubtitle}</span>
          </div>
        </div>
      `;
      this.container.appendChild(comboCard);

      // Hide standalone video section on desktop
      if (videoSection) {
        videoSection.classList.add('vinpop-hide-desktop-video');
      }
    }

    if (isRealQuiz && products.length >= 3) {
      this.injectStarterPackBanner(products.slice(0, 3));
    }
  }

  /**
   * Attache les gestionnaires AJAX aux formulaires d'ajout au panier
   */
  attachAjaxFormHandlers() {
    const forms = this.container.querySelectorAll('.vinpop-ajax-form');
    console.log(`🔧 Attaching AJAX handlers to ${forms.length} forms`);
    
    forms.forEach(form => {
      // Éviter d'attacher plusieurs fois
      if (form.dataset.ajaxAttached) return;
      form.dataset.ajaxAttached = 'true';
      
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const submitBtn = form.querySelector('button[type="submit"]');
        const originalText = submitBtn.innerHTML;
        
        try {
          // Désactiver le bouton et montrer un état de chargement
          submitBtn.disabled = true;
          submitBtn.innerHTML = 'Adding...';
          
          const formData = new FormData(form);
          
          // Ajouter les sections pour le modal cart si nécessaire
          const modalCart = window.theme?.settings?.cartType === 'modal';
          if (modalCart) {
            formData.append('sections', 'cart-items,cart-footer,cart-item-count');
            formData.append('sections_url', window.location.pathname);
          }
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          const data = await response.json();
          
          if (data.status === 422 || data.status === 'error') {
            throw new Error(data.description || data.message || 'Error adding to cart');
          }
          
          // Succès! Afficher un feedback
          submitBtn.innerHTML = '✓ Added';
          submitBtn.style.backgroundColor = 'rgb(34, 197, 94)';
          
          // Déclencher l'événement pour ouvrir le modal cart
          if (modalCart) {
            document.body.dispatchEvent(
              new CustomEvent('shapes:cart:afteradditem', {
                bubbles: true,
                detail: { response: data }
              })
            );
          }
          
          // Réinitialiser le bouton après 2 secondes
          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
            submitBtn.style.backgroundColor = '';
          }, 2000);
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          submitBtn.disabled = false;
          submitBtn.innerHTML = '✗ Error';
          submitBtn.style.backgroundColor = 'rgb(239, 68, 68)';
          
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.style.backgroundColor = '';
          }, 2000);
        }
      });
    });
  }

  /**
   * Bannière sticky bottom "Ajouter mon Top 3 & livraison gratuite"
   */
  injectStarterPackBanner(top3Products) {
    // Remove existing banner if any
    const existing = document.getElementById('vinpop-starter-pack-banner');
    if (existing) existing.remove();
    const existingStyle = document.getElementById('vinpop-starter-pack-banner-styles');
    if (existingStyle) existingStyle.remove();

    // Get the variant IDs for the top 3 available products
    const items = top3Products
      .map(p => {
        const variant = this.getPreferredVariant(p, { requireAvailable: true });
        if (!variant) return null;
        return { id: variant.id, quantity: 1 };
      })
      .filter(Boolean);

    if (items.length === 0) return;

    const isEN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
    const btnText = isEN
      ? '🍷 Add my Top 3 & Unlock Free Shipping'
      : '🍷 Voeg mijn Top 3 toe & Ontgrendel Gratis Verzending';
    const addingText = isEN ? 'Adding...' : 'Toevoegen...';
    const successText = isEN ? '✓ Added to cart!' : '✓ Toegevoegd aan winkelwagen!';
    const errorText = isEN ? '✗ Error, try again' : '✗ Fout, probeer opnieuw';

    // Inject styles
    const style = document.createElement('style');
    style.id = 'vinpop-starter-pack-banner-styles';
    style.textContent = `
      .vinpop-sp-banner {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        padding: 0.85rem 1.5rem;
        background: #000000;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transform: translateY(100%);
        animation: vinpop-sp-banner-slide-up 0.4s ease-out 0.8s forwards;
        transition: opacity 0.15s ease;
      }
      .vinpop-sp-banner:hover {
        opacity: 0.92;
      }
      @keyframes vinpop-sp-banner-slide-up {
        to { transform: translateY(0); }
      }
      .vinpop-sp-banner__text {
        color: #ffffff;
        font-weight: 700;
        font-size: 0.95rem;
        letter-spacing: 0.02em;
        text-align: center;
        white-space: nowrap;
      }
      .vinpop-sp-banner--success {
        background: rgb(34, 197, 94) !important;
      }
      .vinpop-sp-banner--error {
        background: rgb(239, 68, 68) !important;
      }
      .vinpop-sp-banner--disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .vinpop-sp-banner__close {
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        font-size: 1.1rem;
        cursor: pointer;
        color: #ffffff;
        opacity: 0.5;
        padding: 0.25rem 0.5rem;
      }
      .vinpop-sp-banner__close:hover {
        opacity: 1;
      }
      @media (max-width: 749px) {
        .vinpop-sp-banner {
          padding: 0.7rem 0.75rem;
        }
        .vinpop-sp-banner__text {
          font-size: 0.82rem;
        }
      }
    `;
    document.head.appendChild(style);

    // Create banner — text directly in the bar, no button
    const banner = document.createElement('div');
    banner.id = 'vinpop-starter-pack-banner';
    banner.className = 'vinpop-sp-banner';
    banner.innerHTML = `
      <span class="vinpop-sp-banner__text" id="vinpop-sp-banner-text">${btnText}</span>
      <button class="vinpop-sp-banner__close" aria-label="Close" id="vinpop-sp-banner-close">✕</button>
    `;
    document.body.appendChild(banner);

    // Close button
    // Close button (stop propagation so it doesn't trigger add-to-cart)
    document.getElementById('vinpop-sp-banner-close').addEventListener('click', (e) => {
      e.stopPropagation();
      banner.style.transform = 'translateY(100%)';
      banner.style.transition = 'transform 0.3s ease';
      setTimeout(() => banner.remove(), 300);
    });

    // Click the whole banner to add to cart
    let bannerBusy = false;
    banner.addEventListener('click', async () => {
      if (bannerBusy) return;
      bannerBusy = true;
      const textEl = document.getElementById('vinpop-sp-banner-text');
      banner.classList.add('vinpop-sp-banner--disabled');
      textEl.textContent = addingText;

      try {
        // Step 1: Add items to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (data.status === 422 || data.status === 'error') {
          throw new Error(data.description || data.message || 'Error');
        }

        // Step 2: Fetch updated cart WITH sections using /cart/update.js
        // This is the same pattern the Shapes theme uses internally
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname
          })
        });

        const cartData = await updateResponse.json();

        // Success
        textEl.textContent = successText;
        banner.classList.remove('vinpop-sp-banner--disabled');
        banner.classList.add('vinpop-sp-banner--success');

        // Trigger cart modal update — sections are now in the format the Shapes theme expects
        document.body.dispatchEvent(
          new CustomEvent('shapes:cart:afteradditem', {
            bubbles: true,
            detail: {
              response: {
                ...data,
                sections: cartData.sections
              }
            }
          })
        );

        // Hide banner after 2s
        setTimeout(() => {
          banner.style.transform = 'translateY(100%)';
          banner.style.transition = 'transform 0.3s ease';
          setTimeout(() => banner.remove(), 300);
        }, 2000);

      } catch (error) {
        console.error('Error adding top 3 to cart:', error);
        textEl.textContent = errorText;
        banner.classList.remove('vinpop-sp-banner--disabled');
        banner.classList.add('vinpop-sp-banner--error');
        setTimeout(() => {
          bannerBusy = false;
          textEl.textContent = btnText;
          banner.classList.remove('vinpop-sp-banner--error');
        }, 2500);
      }
    });

    console.log('✅ Starter Pack sticky banner injected');
  }

  /**
   * Toggle un filtre (pour les checkboxes)
   * IMPORTANT : event.preventDefault() pour éviter tout rechargement
   */
  toggleFilter(attribute, value, isChecked, event) {
    // BLOQUER le comportement par défaut du navigateur
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log(`🔍 Toggle filter ${attribute}:`, value, isChecked);
    
    if (!this.filters[attribute]) {
      this.filters[attribute] = [];
    }
    
    if (isChecked) {
      // Ajouter la valeur si elle n'existe pas
      if (!this.filters[attribute].includes(value)) {
        this.filters[attribute].push(value);
      }
    } else {
      // Retirer la valeur
      this.filters[attribute] = this.filters[attribute].filter(v => v !== value);
      
      // Supprimer le filtre s'il est vide
      if (this.filters[attribute].length === 0) {
        delete this.filters[attribute];
      }
    }
    
    // Appliquer tous les filtres actifs
    this.applyFilters();
    this.updateActiveFilterCounts();
    this.updateClearButton();
  }

  /**
   * Filtre par attribut (ancienne méthode pour compatibilité)
   */
  filterBy(attribute, value) {
    console.log(`🔍 Filtering by ${attribute}:`, value);
    
    // Si value est null/undefined, on retire ce filtre
    if (!value || value === 'all') {
      delete this.filters[attribute];
    } else {
      this.filters[attribute] = [value];
    }
    
    // Appliquer tous les filtres actifs
    this.applyFilters();
    this.updateActiveFilterCounts();
    this.updateClearButton();
  }

  /**
   * Applique tous les filtres actifs sur les recommandations
   * CORRECTIF: Normalisation des comparaisons (trim + lowercase)
   */
  applyFilters() {
    let filtered = [...this.recommendations];
    
    // Appliquer chaque filtre
    Object.keys(this.filters).forEach(attribute => {
      const values = this.filters[attribute];
      
      // Normaliser les valeurs de filtre (trim + lowercase)
      const normalizedFilterValues = values.map(v => 
        String(v).trim().toLowerCase()
      );
      
      console.log(`🔍 Filtering by ${attribute}:`, normalizedFilterValues);
      
      // Si c'est un tableau (multi-sélection)
      if (Array.isArray(values) && values.length > 0) {
        filtered = filtered.filter(product => {
          switch(attribute) {
            case 'country':
              const productCountry = String(product.country || '').trim().toLowerCase();
              const matchCountry = normalizedFilterValues.includes(productCountry);
              if (!matchCountry) {
                console.log(`  ❌ Product "${product.title}" - country "${product.country}" not in`, values);
              }
              return matchCountry;
            
            case 'region':
              const productRegion = String(product.region || '').trim().toLowerCase();
              return normalizedFilterValues.includes(productRegion);
            
            case 'type':
            case 'wineColor':
              const productType = String(product.type || product.wineColor || '').trim().toLowerCase();
              return normalizedFilterValues.includes(productType);
            
            case 'grapeVariety':
              // CORRECTIF: Gérer le cas où grapeVariety est un tableau
              if (Array.isArray(product.grapeVariety)) {
                const normalizedGrapes = product.grapeVariety.map(g => String(g).trim().toLowerCase());
                return normalizedFilterValues.some(v => normalizedGrapes.includes(v));
              } else if (typeof product.grapeVariety === 'string' && product.grapeVariety.includes(',')) {
                const productGrapes = product.grapeVariety.split(',')
                  .map(g => g.trim().toLowerCase());
                return normalizedFilterValues.some(v => productGrapes.includes(v));
              }
              const singleGrape = String(product.grapeVariety || '').trim().toLowerCase();
              return normalizedFilterValues.includes(singleGrape);
            
            case 'body':
              const productBody = String(product.body || '').trim().toLowerCase();
              return normalizedFilterValues.includes(productBody);
            
            case 'foodPairing':
              // CORRECTIF: Gérer tableaux ET strings avec virgules
              let productPairings = [];
              if (Array.isArray(product.foodPairing)) {
                productPairings = product.foodPairing.map(p => String(p).trim().toLowerCase());
              } else if (typeof product.foodPairing === 'string') {
                productPairings = product.foodPairing.split(',')
                  .map(p => p.trim().toLowerCase());
              }
              return normalizedFilterValues.some(v => productPairings.includes(v));
            
            case 'vendor':
              const productVendor = String(product.vendor || '').trim().toLowerCase();
              return normalizedFilterValues.includes(productVendor);
            
            case 'priceRange':
              // Pour les prix, on accepte si le produit correspond à au moins une fourchette
              return values.some(range => {
                const [min, max] = range.split('-').map(Number);
                const priceEuro = this.getProductDisplayPrice(product) / 100;
                return priceEuro >= min && (max ? priceEuro <= max : true);
              });
            
            case 'minScore':
              // Pour le score, on prend le maximum sélectionné
              const maxScore = Math.max(...values.map(v => parseInt(v)));
              return product.score >= maxScore;
            
            default:
              return true;
          }
        });
      }
    });
    
    console.log(`✅ Filtered: ${filtered.length} / ${this.recommendations.length} products`);
    
    // Réafficher les produits filtrés
    this.displayProducts(filtered);
    this.updateCounter(filtered.length);
  }

  /**
   * Réinitialise tous les filtres
   */
  clearFilters() {
    console.log('🔄 Clearing all filters');
    this.filters = {};
    
    // Décocher toutes les checkboxes
    document.querySelectorAll('.quiz-filter-input').forEach(input => {
      input.checked = false;
    });
    
    this.displayProducts(this.recommendations);
    this.updateCounter();
    this.updateActiveFilterCounts();
    this.updateClearButton();
  }

  /**
   * Met à jour les compteurs de filtres actifs (ex: "Country (2)")
   */
  updateActiveFilterCounts() {
    // Réinitialiser tous les compteurs
    document.querySelectorAll('.quiz-active-count').forEach(span => {
      span.style.display = 'none';
      span.textContent = '';
    });
    
    // Afficher les compteurs pour les filtres actifs
    Object.keys(this.filters).forEach(attribute => {
      const values = this.filters[attribute];
      if (Array.isArray(values) && values.length > 0) {
        const countSpan = document.querySelector(`.quiz-active-count[data-filter="${attribute}"]`);
        if (countSpan) {
          countSpan.textContent = `(${values.length})`;
          countSpan.style.display = 'inline';
        }
      }
    });
  }

  /**
   * Affiche/masque le bouton "Clear all filters"
   */
  updateClearButton() {
    const clearBtn = document.getElementById('quiz-clear-filters');
    if (clearBtn) {
      const hasFilters = Object.keys(this.filters).length > 0;
      clearBtn.style.display = hasFilters ? 'block' : 'none';
    }
  }

  /**
   * Retourne les valeurs uniques d'un attribut (pour générer les options de filtres)
   * CORRECTIF: Gère les tableaux (ex: grape_variety peut contenir plusieurs cépages)
   * et les strings séparées par virgules (ex: food_pairing)
   */
  getUniqueValues(attribute) {
    const values = new Set();
    
    this.recommendations.forEach(product => {
      const rawValue = product[attribute];
      
      // Si pas de valeur, passer au suivant
      if (!rawValue) return;
      
      // CORRECTIF 1: Si c'est un tableau (ex: ['Merlot', 'Cabernet'])
      if (Array.isArray(rawValue)) {
        rawValue.forEach(val => {
          if (val && typeof val === 'string') {
            values.add(val.trim());
          }
        });
        return;
      }
      
      // CORRECTIF 2: Si c'est une string qui contient des virgules (ex: "Merlot, Cabernet")
      if (typeof rawValue === 'string' && rawValue.includes(',')) {
        const items = rawValue.split(',').map(item => item.trim());
        items.forEach(item => {
          if (item) values.add(item);
        });
        return;
      }
      
      // Cas normal: simple string
      if (typeof rawValue === 'string' && rawValue.trim()) {
        values.add(rawValue.trim());
      }
    });
    
    return Array.from(values).sort();
  }

  /**
   * Met à jour le compteur de produits affichés
   */
  updateCounter(count = null) {
    const counter = document.getElementById('quiz-product-counter');
    if (counter) {
      const displayCount = count !== null ? count : this.recommendations.length;
      const totalCount = this.recommendations.length;
      counter.textContent = `${displayCount} wine${displayCount !== 1 ? 's' : ''} ${count !== null ? `of ${totalCount}` : ''}`;
    }
  }

  /**
   * Génère les contrôles de filtrage dynamiquement
   * VERSION STANDALONE - Vanilla JS avec accordéons manuels
   * AVEC FILTRES DYNAMIQUES basés sur les metafields disponibles
   */
  createFilterControls(containerId = 'vinpop-drawer-content') {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Récupérer toutes les valeurs uniques pour chaque attribut
    const countries = this.getUniqueValues('country');
    const types = this.getUniqueValues('type');
    const regions = this.getUniqueValues('region');
    const grapes = this.getUniqueValues('grapeVariety');
    const bodies = this.getUniqueValues('body');
    const pairings = this.getUniqueValues('foodPairing');
    
    // Vider le contenu
    container.innerHTML = '';
    
    // ============ FILTRE PAYS ============
    if (countries.length > 0) {
      const section = this.createFilterSection('country', 'Country', countries);
      container.appendChild(section);
    }
    
    // ============ FILTRE RÉGION ============
    if (regions.length > 0) {
      const section = this.createFilterSection('region', 'Region', regions);
      container.appendChild(section);
    }
    
    // ============ FILTRE TYPE/COULEUR ============
    if (types.length > 0) {
      const section = this.createFilterSection('type', 'Wine Type', types);
      container.appendChild(section);
    }
    
    // ============ FILTRE CÉPAGE ============
    if (grapes.length > 0) {
      const section = this.createFilterSection('grapeVariety', 'Grape Variety', grapes);
      container.appendChild(section);
    }
    
    // ============ FILTRE CORPS ============
    if (bodies.length > 0) {
      const section = this.createFilterSection('body', 'Body', bodies);
      container.appendChild(section);
    }
    
    // ============ FILTRE ACCORDS METS ============
    if (pairings.length > 0) {
      const section = this.createFilterSection('foodPairing', 'Food Pairing', pairings);
      container.appendChild(section);
    }
    
    // ============ FILTRE PRIX ============
    const priceSection = this.createPriceFilterSection();
    container.appendChild(priceSection);
    
    // ============ FILTRE SCORE MINIMUM ============
    const scoreSection = this.createScoreFilterSection();
    container.appendChild(scoreSection);
    
    // ============ BOUTON CLEAR ============
    const clearBtn = document.createElement('button');
    clearBtn.id = 'quiz-clear-filters';
    clearBtn.textContent = 'Clear all filters';
    clearBtn.style.display = 'none';
    clearBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.clearFilters();
    });
    container.appendChild(clearBtn);
    
    // ============ COMPTEUR ============
    const counter = document.createElement('div');
    counter.style.marginTop = '20px';
    counter.style.padding = '12px';
    counter.style.borderTop = '1px solid #eee';
    counter.innerHTML = `
      <div style="font-size: 14px;">
        Showing <strong id="quiz-product-counter">0 wines</strong>
      </div>
    `;
    container.appendChild(counter);
    
    console.log('✅ Filter controls created (standalone)');
    
    // IMPORTANT : Attacher les event listeners IMMÉDIATEMENT
    this.attachFilterListeners();
  }
  
  /**
   * Crée une section de filtre avec accordéon vanilla JS
   */
  createFilterSection(filterType, title, values) {
    const section = document.createElement('div');
    section.className = 'vinpop-filter-section';
    
    // Header cliquable
    const header = document.createElement('button');
    header.className = 'vinpop-filter-header';
    header.innerHTML = `
      <span>
        ${title}
        <span class="quiz-active-count" data-filter="${filterType}" style="display:none; margin-left:5px; color:#666;"></span>
      </span>
      <span class="vinpop-filter-arrow">▼</span>
    `;
    
    // Content (caché par défaut)
    const content = document.createElement('div');
    content.className = 'vinpop-filter-content';
    
    // Créer les checkboxes
    values.forEach((value, index) => {
      const item = document.createElement('label');
      item.className = 'vinpop-checkbox-item';
      
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'quiz-filter-input';
      checkbox.id = `quiz-filter-${filterType}-${index}`;
      checkbox.dataset.filterType = filterType;
      checkbox.dataset.value = value;
      
      const label = document.createElement('span');
      label.textContent = value;
      
      item.appendChild(checkbox);
      item.appendChild(label);
      content.appendChild(item);
    });
    
    // Toggle accordéon
    header.addEventListener('click', (e) => {
      e.preventDefault();
      const arrow = header.querySelector('.vinpop-filter-arrow');
      const isOpen = content.classList.contains('is-open');
      
      if (isOpen) {
        content.classList.remove('is-open');
        arrow.classList.remove('is-open');
      } else {
        content.classList.add('is-open');
        arrow.classList.add('is-open');
      }
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }
  
  /**
   * Crée la section de filtre des prix
   */
  createPriceFilterSection() {
    // Calculate dynamic price ranges from actual product prices
    const prices = (this.recommendations || [])
      .map(p => (p.price || 0) / 100)
      .filter(p => p > 0);
    
    let priceRanges;
    if (prices.length >= 2) {
      const minPrice = Math.floor(Math.min(...prices));
      const maxPrice = Math.ceil(Math.max(...prices));
      const step = Math.ceil((maxPrice - minPrice) / 4);
      const b1 = minPrice + step;
      const b2 = minPrice + step * 2;
      const b3 = minPrice + step * 3;
      priceRanges = [
        { label: `€${minPrice} - €${b1}`, value: `${minPrice}-${b1}` },
        { label: `€${b1} - €${b2}`, value: `${b1}-${b2}` },
        { label: `€${b2} - €${b3}`, value: `${b2}-${b3}` },
        { label: `€${b3} - €${maxPrice}`, value: `${b3}-${maxPrice}` }
      ];
    } else {
      priceRanges = [
        { label: 'Under €25', value: '0-25' },
        { label: '€25 - €50', value: '25-50' },
        { label: '€50 - €100', value: '50-100' },
        { label: 'Over €100', value: '100-999' }
      ];
    }
    
    const section = document.createElement('div');
    section.className = 'vinpop-filter-section';
    
    const header = document.createElement('button');
    header.className = 'vinpop-filter-header';
    header.innerHTML = `
      <span>
        Price Range
        <span class="quiz-active-count" data-filter="priceRange" style="display:none; margin-left:5px; color:#666;"></span>
      </span>
      <span class="vinpop-filter-arrow">▼</span>
    `;
    
    const content = document.createElement('div');
    content.className = 'vinpop-filter-content';
    
    priceRanges.forEach((range, index) => {
      // Count products in this range
      const [rMin, rMax] = range.value.split('-').map(Number);
      const count = prices.filter(p => p >= rMin && p <= rMax).length;
      if (count === 0) return; // Hide empty ranges

      const item = document.createElement('label');
      item.className = 'vinpop-checkbox-item';
      
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'quiz-filter-input';
      checkbox.id = `quiz-filter-price-${index}`;
      checkbox.dataset.filterType = 'priceRange';
      checkbox.dataset.value = range.value;
      
      const label = document.createElement('span');
      label.textContent = `${range.label} (${count})`;
      
      item.appendChild(checkbox);
      item.appendChild(label);
      content.appendChild(item);
    });
    
    header.addEventListener('click', (e) => {
      e.preventDefault();
      const arrow = header.querySelector('.vinpop-filter-arrow');
      content.classList.toggle('is-open');
      arrow.classList.toggle('is-open');
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }
  
  /**
   * Crée la section de filtre des scores
   */
  createScoreFilterSection() {
    const scoreRanges = [
      { label: '80%+', value: '80' },
      { label: '90%+', value: '90' },
      { label: '95%+', value: '95' }
    ];
    
    const section = document.createElement('div');
    section.className = 'vinpop-filter-section';
    
    const header = document.createElement('button');
    header.className = 'vinpop-filter-header';
    header.innerHTML = `
      <span>
        Match Score
        <span class="quiz-active-count" data-filter="minScore" style="display:none; margin-left:5px; color:#666;"></span>
      </span>
      <span class="vinpop-filter-arrow">▼</span>
    `;
    
    const content = document.createElement('div');
    content.className = 'vinpop-filter-content';
    
    const scores = (this.recommendations || []).map(p => p.score || 0);

    scoreRanges.forEach((range, index) => {
      const minVal = parseInt(range.value);
      const count = scores.filter(s => s >= minVal).length;
      if (count === 0) return; // Hide empty ranges

      const item = document.createElement('label');
      item.className = 'vinpop-checkbox-item';
      
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'quiz-filter-input';
      checkbox.id = `quiz-filter-score-${index}`;
      checkbox.dataset.filterType = 'minScore';
      checkbox.dataset.value = range.value;
      
      const label = document.createElement('span');
      label.textContent = `${range.label} (${count})`;
      
      item.appendChild(checkbox);
      item.appendChild(label);
      content.appendChild(item);
    });
    
    header.addEventListener('click', (e) => {
      e.preventDefault();
      const arrow = header.querySelector('.vinpop-filter-arrow');
      content.classList.toggle('is-open');
      arrow.classList.toggle('is-open');
    });
    
    section.appendChild(header);
    section.appendChild(content);
    
    return section;
  }
  attachFilterListeners() {
    const inputs = document.querySelectorAll('.quiz-filter-input');
    
    inputs.forEach(input => {
      input.addEventListener('change', (event) => {
        // BLOQUER tout comportement par défaut
        event.preventDefault();
        event.stopPropagation();
        
        const filterType = input.dataset.filterType;
        const value = input.dataset.value;
        const isChecked = input.checked;
        
        this.toggleFilter(filterType, value, isChecked, event);
      });
    });
    
    console.log('✅ Filter listeners attached to', inputs.length, 'inputs');
  }
}

// Instance globale
window.wineManager = new WineRecommendationManager();

console.log('✅ Wine Recommendation Manager ready');

// ============ PERSISTANCE : RESTAURATION AU CHARGEMENT ============
// DUAL-MODE SYSTEM: Handles both Quiz (/collections/step-1-taste-tuner) and Wine Assistant (/collections/my-playlist)
function checkAndRestoreResults() {
  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const hasQuizParams = urlParams.has('taste_profile');
  const hasSourceProduct = urlParams.has('source_product');
  
  // STRICT SCOPING: Only activate on specific collections
  // Exclude /collections/quizz-tasting (handled by its own independent quiz system)
  const isQuizPage = pathname.includes('/collections/step-1-taste-tuner');
  // Wine Assistant: ONLY /collections/my-playlist (NOT wine-assistant), and NO source_product
  // Note: /collections/wine-assistant is ALWAYS handled by collection-recommendations.js
  // Use endsWith to handle language prefixes like /nl/collections/my-playlist
  const isAssistantPage = (pathname === '/collections/my-playlist' || pathname.endsWith('/collections/my-playlist')) && !hasSourceProduct && !pathname.includes('wine-assistant');
  
  console.log('🔍 Checking restoration:', { 
    pathname,
    isQuizPage,
    isAssistantPage,
    hasQuizParams,
    hasSourceProduct,
    url: window.location.href
  });
  
  // EXIT: Not on a managed collection
  if (!isQuizPage && !isAssistantPage) {
    console.log('👁️ Not on Quiz or Wine Assistant page - skipping');
    return;
  }
  
  // ========== QUIZ MODE ==========
  if (isQuizPage) {
    // V2 taste map handles all rendering on the quizz page.
    // It will call renderRecommendations() directly after fetching wine data.
    window.wineManager.mode = 'taste-map';
    console.log('🍷 Quiz page: V2 taste map will trigger rendering');
    return;
  }
  
  // ========== WINE ASSISTANT MODE ==========
  if (isAssistantPage) {
    window.wineManager.mode = 'assistant';
    // Toujours afficher la liste complète des produits Shopify avec scores
    console.log('🍷 Wine Assistant mode activated (full product list)');
    const initAssistant = async () => {
      try {
        const wines = await window.wineManager.fetchUserRecommendations();
        if (wines && wines.length > 0) {
          await window.wineManager.renderRecommendations(wines);
          console.log('✅ Wine Assistant recommendations loaded');
        } else {
          console.log('⚠️ No Wine Assistant recommendations available');
        }
      } catch (error) {
        console.error('❌ Failed to initialize Wine Assistant:', error);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAssistant);
    } else {
      initAssistant();
    }
    return;
  }
}

// Restauration au chargement initial
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkAndRestoreResults);
} else {
  checkAndRestoreResults();
}

// ============ GESTION DU CACHE NAVIGATEUR (BFCACHE) ============
// Gérer le bouton "Retour" du navigateur - DUAL MODE SUPPORT
window.addEventListener('pageshow', (event) => {
  const pathname = window.location.pathname;
  const isQuizPage = pathname.includes('/collections/step-1-taste-tuner');
  const isAssistantPage = pathname.includes('/collections/my-playlist') && !pathname.includes('/collections/wine-assistant');
  
  // STRICT SCOPING: Only on managed pages
  if (!isQuizPage && !isAssistantPage) {
    return;
  }
  
  // Si la page est restaurée du cache (bouton retour)
  if (event.persisted || (performance.getEntriesByType("navigation")[0] && performance.getEntriesByType("navigation")[0].type === "back_forward")) {
    // Quiz page: V2 handles rendering via its own init
    if (isQuizPage) {
      console.log('♻️ Quiz page restored from bfcache — V2 will re-init');
      return;
    }
    
    console.log(`♻️ Page restored from bfcache (back button) - WINE ASSISTANT`);
    
    const savedData = sessionStorage.getItem('vinpop_assistant_results');
    
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        window.wineManager.mode = 'assistant';
        window.wineManager.renderRecommendations(data)
          .then(() => console.log('✅ Results restored after back button'))
          .catch(err => console.error('❌ Render failed after back button:', err));
      } catch (err) {
        console.error('❌ Error restoring results after back button:', err);
      }
    }
  }
});
